// ANALYTICS-024: Mirror mw_visitor_id (already in localStorage for HotTakePoll) and
// mw_session_id into cookies on first analytics emit so the backend can attribute
// each event to a stable visitor + browser session without touching every emitter.
// Visitor cookie is long-lived (1y); session cookie has no Max-Age so it dies on
// browser close, matching sessionStorage semantics. Run once on module load —
// sendBeacon is same-origin, so the credentialed POST carries these cookies.
function ensureAnalyticsCookies(): void {
  if (typeof document === 'undefined') return;
  if (!/(?:^|;\s*)mw_visitor_id=/.test(document.cookie)) {
    let id = '';
    try {
      id = window.localStorage.getItem('mw_visitor_id') || '';
    } catch {
      // localStorage may be blocked (private mode); fall through to UUID
    }
    if (!id) {
      id = crypto.randomUUID();
      try { window.localStorage.setItem('mw_visitor_id', id); } catch { /* ignore */ }
    }
    document.cookie = `mw_visitor_id=${id}; Path=/; Max-Age=31536000; SameSite=Lax`;
  }
  if (!/(?:^|;\s*)mw_session_id=/.test(document.cookie)) {
    document.cookie = `mw_session_id=${crypto.randomUUID()}; Path=/; SameSite=Lax`;
  }
}

if (typeof window !== 'undefined') {
  ensureAnalyticsCookies();
}

// ANALYTICS-082: Session age (ms since first page load) so Pro CTA funnel events
// can be sliced by how long the user browsed before encountering/clicking a CTA.
const SESSION_START_KEY = 'mw_session_start_ts';
function getSessionAgeMs(): number {
  if (typeof sessionStorage === 'undefined') return 0;
  try {
    let start = Number(sessionStorage.getItem(SESSION_START_KEY));
    if (!(start > 0)) {
      start = Date.now();
      sessionStorage.setItem(SESSION_START_KEY, String(start));
    }
    return Date.now() - start;
  } catch {
    return 0;
  }
}

// ANALYTICS-059: UTM Source Attribution — capture inbound UTM params on first
// page load and persist in sessionStorage so every analytics event in the
// session carries attribution data. Only captured once per session (first
// touch); subsequent navigations don't overwrite.
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
type UtmAttribution = Partial<Record<(typeof UTM_KEYS)[number], string>>;

let _utmCache: UtmAttribution | null = null;

function captureUtmAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    const existing = window.sessionStorage.getItem('mw_utm');
    if (existing) {
      _utmCache = JSON.parse(existing);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const utm: UtmAttribution = {};
    let hasAny = false;
    for (const key of UTM_KEYS) {
      const val = params.get(key);
      if (val) { utm[key] = val; hasAny = true; }
    }
    if (hasAny) {
      window.sessionStorage.setItem('mw_utm', JSON.stringify(utm));
      _utmCache = utm;
      // Fire a one-time attribution event so we can track landing sessions
      const event = {
        event: 'utm_attribution' as const,
        ...utm,
        landingPage: window.location.pathname,
        timestamp: Date.now(),
      };
      if (import.meta.env.DEV) console.log('[analytics]', event);
      try {
        navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
      } catch { /* ignore */ }
    }
  } catch { /* sessionStorage blocked — silently ignore */ }
}

if (typeof window !== 'undefined') {
  captureUtmAttribution();
}

/** Returns cached UTM attribution for the current session, or empty object. */
export function getUtmAttribution(): UtmAttribution {
  if (_utmCache) return _utmCache;
  try {
    const stored = typeof window !== 'undefined' ? window.sessionStorage.getItem('mw_utm') : null;
    if (stored) { _utmCache = JSON.parse(stored); return _utmCache!; }
  } catch { /* ignore */ }
  return {};
}

// Affiliate CTA click tracking
// ANALYTICS-039: vehicleCategory carries the post's vehicle category (e.g. "cars",
// "motorcycles", "ev") so affiliate revenue can be segmented by vehicle type.
interface AffiliateClickEvent {
  event: 'affiliate_cta_clicked';
  brand: string;
  postId: string;
  source: 'feed' | 'detail' | 'postcard' | 'bike-page';
  position?: number;
  vehicleCategory?: string;
  productCategory?: string;
  segment: VehicleSegment;
  timestamp: number;
}

export function trackAffiliateClick(
  brand: string,
  postId: string,
  source: 'feed' | 'detail' | 'postcard' | 'bike-page',
  position?: number,
  vehicleCategory?: string,
): void {
  const pagePath = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const productCategory = vehicleCategory ?? inferCategory(pagePath);
  const event: AffiliateClickEvent = {
    event: 'affiliate_cta_clicked',
    brand,
    postId,
    source,
    position,
    ...(vehicleCategory && { vehicleCategory }),
    ...(productCategory && { productCategory }),
    segment: toVehicleSegment(vehicleCategory || productCategory),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Page view tracking
interface PageViewEvent {
  event: 'page_viewed';
  pageType: string;
  pageId: string;
  timestamp: number;
}

export function trackPageView(pageType: string, pageId: string): void {
  const event: PageViewEvent = {
    event: 'page_viewed',
    pageType,
    pageId,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

type ShareChannel = 'twitter' | 'facebook' | 'whatsapp' | 'linkedin' | 'copy' | 'native';

interface ShareEvent {
  event: 'post_shared';
  postId: string;
  postTitle: string;
  channel: ShareChannel;
  category?: string;
  sourceId?: string;
  timestamp: number;
}

export function trackShare(
  channel: ShareChannel,
  postId: string,
  postTitle: string,
  category?: string,
  sourceId?: string
): void {
  const event: ShareEvent = {
    event: 'post_shared',
    postId,
    postTitle,
    channel,
    category,
    sourceId,
    timestamp: Date.now(),
  };

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  // Send to backend analytics endpoint (fire and forget)
  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Search query tracking
interface SearchQueryEvent {
  event: 'search_query';
  query: string;
  searchType: string;
  timestamp: number;
}

export function trackSearchQuery(query: string, searchType: string): void {
  const event: SearchQueryEvent = {
    event: 'search_query',
    query,
    searchType,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-048: Search result tracking — fires when search results load,
// captures result counts and flags zero-result queries for content gap analysis
export function trackSearchResults(
  query: string,
  searchType: string,
  resultCounts: { posts: number; garages: number; users: number; sources: number },
): void {
  const totalResults = resultCounts.posts + resultCounts.garages + resultCounts.users + resultCounts.sources;
  const event = {
    event: totalResults === 0 ? 'search_zero_results' : 'search_results',
    query,
    searchType,
    resultCounts,
    totalResults,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Read-complete tracking — fired when user scrolls to end of article
interface ReadCompleteEvent {
  event: 'read_complete';
  postId: string;
  readingTimeMs: number;
  timestamp: number;
}

export function trackReadComplete(postId: string, readingTimeMs: number): void {
  const event: ReadCompleteEvent = {
    event: 'read_complete',
    postId,
    readingTimeMs,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-058: Article category performance — fires on read-complete with category
// dimension so we can measure which content segments (EV, Bikes, MotoGP, Launches,
// Industry) drive retention. Pairs with read_complete but adds category + scroll depth.
interface ArticleCategoryReadEvent {
  event: 'article_category_read';
  postId: string;
  category: string;
  timeOnPageSeconds: number;
  scrollDepthPct: number;
  timestamp: number;
}

export function trackArticleCategoryRead(
  postId: string,
  category: string,
  timeOnPageSeconds: number,
  scrollDepthPct: number,
): void {
  const event: ArticleCategoryReadEvent = {
    event: 'article_category_read',
    postId,
    category,
    timeOnPageSeconds: Math.round(timeOnPageSeconds),
    scrollDepthPct: Math.round(scrollDepthPct),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Garage share tracking — fired when GarageShareModal opens or user confirms sharing
interface GarageShareEvent {
  event: 'garage_share_open' | 'garage_share_confirm';
  postId: string;
  garageId?: string;
  timestamp: number;
}

export function trackGarageShare(
  action: 'garage_share_open' | 'garage_share_confirm',
  postId: string,
  garageId?: string
): void {
  const event: GarageShareEvent = {
    event: action,
    postId,
    garageId,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-044: Unified Pro CTA impression + click events.
// Prerequisite for MON proposals — the existing per-surface trackers
// (trackProUpsellImpression, trackUpgradePromptShown, trackProUpsellView, …)
// share no enum across files, so dashboards have to join 5 event names to
// answer "where do users see/click Pro CTAs?". These two events emit a single
// normalized `location` so the baseline can be sliced from one stream.
// Granular events stay in place; this layer is additive.
export type ProCtaLocation = 'feed' | 'garage' | 'article' | 'profile' | 'sidebar' | 'bookmarks';

export function trackProCtaImpression(location: ProCtaLocation, ctaVariant?: string): void {
  const event = {
    event: 'pro_cta_impression' as const,
    location,
    ...(ctaVariant && { ctaVariant }),
    sessionAge: getSessionAgeMs(),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackProCtaClick(location: ProCtaLocation, ctaVariant?: string): void {
  const event = {
    event: 'pro_cta_click' as const,
    location,
    ...(ctaVariant && { ctaVariant }),
    sessionAge: getSessionAgeMs(),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Pro upgrade CTA click tracking
interface ProUpgradeClickEvent {
  event: 'pro_upgrade_click';
  source: string;
  timestamp: number;
}

export function trackProUpgradeClick(source: string): void {
  const event: ProUpgradeClickEvent = {
    event: 'pro_upgrade_click',
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-082: fires when user starts the payment/upgrade flow (distinct from
// pro_checkout_start which fires on Stripe redirect). This event carries
// source_page + cta_variant + session_age so monetization funnels have a
// baseline before MON-003/MON-004 A/B tests.
export function trackProUpgradeStarted(
  sourcePage: ProCtaLocation,
  ctaVariant?: string,
): void {
  // ANALYTICS-103: record funnel step for drop-off tracking
  recordProFunnelStep('upgrade_started');

  const event = {
    event: 'pro_upgrade_started' as const,
    sourcePage,
    ...(ctaVariant && { ctaVariant }),
    sessionAge: getSessionAgeMs(),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Pro upsell banner impression + dismiss tracking (MON-003)
// MON-005: optional `position` carries feed slot (or 'top' for grid above-fold) for funnel CVR
export function trackProUpsellImpression(variantIndex: number, position?: number | 'top'): void {
  const event = {
    event: 'pro_upsell_impression' as const,
    variantIndex,
    position,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackProUpsellDismiss(variantIndex: number): void {
  const event = {
    event: 'pro_upsell_dismiss' as const,
    variantIndex,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// MON-005: dedicated upsell-click event carrying variant + position so the
// impression → click → checkout_start → success funnel can be sliced per slot.
export function trackProUpsellClick(variantIndex: number, position?: number | 'top'): void {
  const event = {
    event: 'pro_upsell_click' as const,
    variantIndex,
    position,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-022: UTM attribution carried with Pro checkout funnel events so
// conversion can be sliced per traffic source / SEO article / campaign.
// trafficSource defaults to 'direct' when no utm_source is present so every
// checkout event is attributable (vs. a missing field that gets filtered out).
export interface ProCheckoutAttribution {
  trafficSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  // ANALYTICS-102: waitlist surface that led to this checkout (if any)
  waitlistSource?: WaitlistSource;
}

const PRO_ATTRIBUTION_STORAGE_KEY = 'mw_pro_attribution';

export function readProAttributionFromUrl(search: string): ProCheckoutAttribution {
  const params = new URLSearchParams(search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const attribution: ProCheckoutAttribution = {};
  if (utmSource) attribution.trafficSource = utmSource;
  if (utmMedium) attribution.utmMedium = utmMedium;
  if (utmCampaign) attribution.utmCampaign = utmCampaign;
  return attribution;
}

// Captures incoming UTM params on /pro entry. If the URL has any UTM, persist
// it for the session so downstream checkout_complete (which lands on
// ?session=success without UTMs) can still attribute. Falls back to the stored
// value when the current URL has no UTMs.
export function captureProAttribution(): ProCheckoutAttribution {
  if (typeof window === 'undefined') return {};
  const fresh = readProAttributionFromUrl(window.location.search);
  const hasFresh = !!(fresh.trafficSource || fresh.utmMedium || fresh.utmCampaign);
  if (hasFresh) {
    try {
      window.sessionStorage.setItem(PRO_ATTRIBUTION_STORAGE_KEY, JSON.stringify(fresh));
    } catch {
      // sessionStorage may be unavailable (private mode, SSR) — non-fatal.
    }
    return fresh;
  }
  try {
    const stored = window.sessionStorage.getItem(PRO_ATTRIBUTION_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as ProCheckoutAttribution;
  } catch {
    // ignore — attribution is best-effort
  }
  // ANALYTICS-102: attach waitlist source if available (even without UTM)
  const wlSource = getWaitlistSource();
  if (wlSource) return { waitlistSource: wlSource };
  return {};
}

// MON-005: fires the moment the user clicks "Get Pro" on /pro before the
// Stripe redirect — distinguishes intent-to-checkout from the upgrade-click
// generic event.
// ANALYTICS-022: optional `attribution` carries UTM source/medium/campaign so
// SEO articles and campaigns can be CVR-attributed.
export function trackProCheckoutStart(
  billingCycle: 'monthly' | 'annual',
  attribution?: ProCheckoutAttribution,
): void {
  // ANALYTICS-103: record funnel step for drop-off tracking
  recordProFunnelStep('checkout_start');

  const event = {
    event: 'pro_checkout_start' as const,
    billingCycle,
    trafficSource: attribution?.trafficSource ?? 'direct',
    ...(attribution?.utmMedium && { utmMedium: attribution.utmMedium }),
    ...(attribution?.utmCampaign && { utmCampaign: attribution.utmCampaign }),
    // ANALYTICS-102: carry waitlist source through checkout funnel
    ...(attribution?.waitlistSource && { waitlistSource: attribution.waitlistSource }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-031: Pro subscription conversion funnel — unified view → click →
// trial_start → complete events that compose across every Pro upsell surface.
// Per-surface events (trackProUpsellImpression, trackUpgradePromptShown,
// trackProCheckoutStart, …) remain in place; the funnel events below give
// dashboards a single series to slice CVR by surface without joining heterogeneous
// event names. trackProUpsellView and trackProTrialStart are intentionally thin
// so callers can adopt them alongside their existing tracking.
export type ProFunnelSurface =
  | 'feed_banner'
  | 'feed_top_banner'
  | 'sidebar'
  | 'pro_page'
  | 'comment_gate'
  | 'newsletter_modal'
  | 'post_detail'
  | 'sticky_bar';

export function trackProUpsellView(surface: ProFunnelSurface, variantIndex?: number): void {
  const event = {
    event: 'pro_upsell_view' as const,
    surface,
    ...(variantIndex !== undefined && { variantIndex }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackProTrialStart(
  billingCycle: 'monthly' | 'annual',
  surface: ProFunnelSurface,
  attribution?: ProCheckoutAttribution,
): void {
  const event = {
    event: 'pro_trial_start' as const,
    billingCycle,
    surface,
    trafficSource: attribution?.trafficSource ?? 'direct',
    ...(attribution?.utmMedium && { utmMedium: attribution.utmMedium }),
    ...(attribution?.utmCampaign && { utmCampaign: attribution.utmCampaign }),
    // ANALYTICS-102: carry waitlist source through checkout funnel
    ...(attribution?.waitlistSource && { waitlistSource: attribution.waitlistSource }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Affiliate CTA click-through on SEO pages — distinct from generic feed clicks
// MON-004: `page` is the source pathname (e.g. /bikes/yamaha-aerox-e-india-launch)
// so revenue attribution can be sliced per spec page, not just per CTA type.
// MON-009: 'test-drive' added to mirror the impression union so TestDriveCTA
// emits a distinct click event (was previously masquerading as 'check-price').
interface AffiliateCTAClickEvent {
  event: 'affiliate_cta_click';
  model: string;
  ctaType: 'insurance' | 'emi' | 'check-price' | 'test-drive';
  placement?: CTAPlacement;
  variantId?: AffiliateVariantId;
  page?: string;
  category?: string;
  segment: VehicleSegment;
  scrollDepth?: number;
  timestamp: number;
}

function inferCategory(path?: string): string | undefined {
  if (!path) return undefined;
  if (path.startsWith('/bikes') || path.startsWith('/scooters')) return 'two-wheeler';
  if (path.startsWith('/cars')) return 'four-wheeler';
  if (path.startsWith('/ev')) return 'ev';
  if (path.startsWith('/guides/f1-') || path.startsWith('/guides/motogp-')) return 'motorsport';
  return undefined;
}

// ANALYTICS-044: Normalise vehicle type/category values into a stable segment
// so affiliate CPL revenue can be sliced by cars / bikes / scooters / evs / luxury.
export type VehicleSegment = 'cars' | 'bikes' | 'scooters' | 'evs' | 'luxury' | 'unknown';

export function toVehicleSegment(raw?: string): VehicleSegment {
  if (!raw) return 'unknown';
  const v = raw.toLowerCase();
  if (v === 'cars' || v === 'four-wheeler') return 'cars';
  if (v === 'motorcycles' || v === 'bikes' || v === 'two-wheeler') return 'bikes';
  if (v === 'scooters') return 'scooters';
  if (v === 'ev' || v === 'evs' || v === 'electric') return 'evs';
  if (v === 'luxury') return 'luxury';
  return 'unknown';
}

// ANALYTICS-023: variant_id distinguishes which sticky/affiliate CTA flavor was
// shown so impression→click CTR can be computed *per variant*, not just per
// page/ctaType. Underscored values (check_price etc.) are the dashboard-stable
// canonical form and are intentionally distinct from the hyphenated ctaType
// values that pre-date the variant rollout.
export type AffiliateVariantId = 'check_price' | 'insurance' | 'emi' | 'test_drive';

// ANALYTICS-047: CTA placement metadata — describes WHERE on the page an
// affiliate CTA is rendered, separate from ctaType (WHAT it offers).
export type CTAPlacement =
  | 'spec-page'
  | 'article'
  | 'article-inline'
  | 'article-sticky'
  | 'compare-footer'
  | 'guide-banner'
  | 'launch-page'
  | 'sidebar'
  | 'feed';

// ANALYTICS-019: Affiliate CTA impression tracking — fires when a CTA component
// enters the viewport (separate from clicks). Lets us compute impression→click
// CTR per CTA type and per page; standard affiliate metric.
interface AffiliateCTAImpressionEvent {
  event: 'affiliate_cta_impression';
  model: string;
  ctaType: 'insurance' | 'emi' | 'check-price' | 'test-drive' | 'sticky';
  placement?: CTAPlacement;
  variantId?: AffiliateVariantId;
  page?: string;
  category?: string;
  timestamp: number;
}

export function trackAffiliateImpression(
  model: string,
  ctaType: 'insurance' | 'emi' | 'check-price' | 'test-drive' | 'sticky',
  page?: string,
  opts?: { variantId?: AffiliateVariantId; placement?: CTAPlacement },
): void {
  const resolvedPage = page ?? (typeof window !== 'undefined' ? window.location.pathname : undefined);
  const event: AffiliateCTAImpressionEvent = {
    event: 'affiliate_cta_impression',
    model,
    ctaType,
    ...(opts?.placement && { placement: opts.placement }),
    ...(opts?.variantId && { variantId: opts.variantId }),
    page: resolvedPage,
    category: inferCategory(resolvedPage),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackAffiliateCTAClick(
  model: string,
  ctaType: 'insurance' | 'emi' | 'check-price' | 'test-drive',
  page?: string,
  opts?: { scrollDepth?: number; variantId?: AffiliateVariantId; vehicleCategory?: string; placement?: CTAPlacement },
): void {
  const resolvedPage = page ?? (typeof window !== 'undefined' ? window.location.pathname : undefined);
  const resolvedCategory = opts?.vehicleCategory || inferCategory(resolvedPage);
  const event: AffiliateCTAClickEvent = {
    event: 'affiliate_cta_click',
    model,
    ctaType,
    ...(opts?.placement && { placement: opts.placement }),
    page: resolvedPage,
    category: resolvedCategory,
    segment: toVehicleSegment(resolvedCategory),
    ...(opts?.scrollDepth != null && { scrollDepth: opts.scrollDepth }),
    ...(opts?.variantId && { variantId: opts.variantId }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Garage engagement tracking — member joins, article saves, invite conversions, DAU/MAU
type GarageEngagementAction = 'garage_member_joined' | 'garage_article_saved' | 'garage_invite_converted' | 'garage_dau_mau';

interface GarageEngagementEvent {
  event: GarageEngagementAction;
  garageId: string;
  userId?: string;
  referrerId?: string;
  timestamp: number;
}

export function trackGarageEngagement(
  action: GarageEngagementAction,
  garageId: string,
  userId?: string,
  referrerId?: string,
): void {
  const event: GarageEngagementEvent = {
    event: action,
    garageId,
    userId,
    referrerId,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Garage post share funnel tracking — tracks each step of the share-to-garage flow
type GaragePostShareStep = 'initiated' | 'modal_opened' | 'completed' | 'link_copied';

interface GaragePostShareEvent {
  event: 'garage_post_share';
  postId: string;
  garageSlug: string;
  step: GaragePostShareStep;
  timestamp: number;
  // ANALYTICS-083: garage + article category dimensions for save funnel attribution
  garageName?: string;
  articleCategory?: string;
}

export function trackGaragePostShare(
  postId: string,
  garageSlug: string,
  step: GaragePostShareStep,
  garageName?: string,
  articleCategory?: string,
): void {
  const event: GaragePostShareEvent = {
    event: 'garage_post_share',
    postId,
    garageSlug,
    step,
    timestamp: Date.now(),
    ...(garageName && { garageName }),
    ...(articleCategory && { articleCategory }),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-048: Feed category interaction tracking — personalization baseline.
// Fires on post click, upvote, downvote in feed context so per-user category
// affinity scores can be computed from the event stream.
type FeedInteractionAction = 'click' | 'upvote' | 'downvote';

// ANALYTICS-092: Extract bare domain from a source URL for publisher attribution.
function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url || 'unknown';
  }
}

interface FeedInteractionEvent {
  event: 'feed_interaction';
  action: FeedInteractionAction;
  postId: string;
  category: string;
  source_domain: string;
  source_name: string;
  timestamp: number;
}

export function trackFeedInteraction(
  action: FeedInteractionAction,
  postId: string,
  category: string,
  sourceUrl: string = 'unknown',
  sourceName: string = 'unknown',
): void {
  const event: FeedInteractionEvent = {
    event: 'feed_interaction',
    action,
    postId,
    category: category || 'unknown',
    source_domain: extractDomain(sourceUrl),
    source_name: sourceName || 'unknown',
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Feed filter usage tracking — fired when user changes feed type, category, or source mode
type FeedFilterType = 'feedType' | 'category' | 'sourceMode' | 'tag' | 'language' | 'fuelType';

interface FeedFilterEvent {
  event: 'feed_filter_used';
  filterType: FeedFilterType;
  value: string;
  timestamp: number;
}

export function trackFeedFilter(
  filterType: FeedFilterType,
  value: string,
): void {
  const event: FeedFilterEvent = {
    event: 'feed_filter_used',
    filterType,
    value,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Scroll depth tracking — fired at 25/50/75/100% milestones on vehicle pages
type ScrollMilestone = 25 | 50 | 75 | 100;

interface ScrollDepthEvent {
  event: 'scroll_depth';
  pageType: string;
  pageId: string;
  depth: ScrollMilestone;
  timestamp: number;
}

export function trackScrollDepth(
  pageType: string,
  pageId: string,
  depth: ScrollMilestone,
): void {
  const event: ScrollDepthEvent = {
    event: 'scroll_depth',
    pageType,
    pageId,
    depth,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Article category read-depth tracking — scroll depth per article + category tag
type ReadDepthMilestone = 25 | 50 | 75 | 100;

interface ReadDepthEvent {
  event: 'read_depth';
  articleId: string;
  category: string;
  depthPercent: ReadDepthMilestone;
  timeToDepthMs: number;
  timestamp: number;
}

export function trackReadDepth(
  articleId: string,
  category: string,
  depthPercent: ReadDepthMilestone,
  timeToDepthMs: number,
): void {
  const event: ReadDepthEvent = {
    event: 'read_depth',
    articleId,
    category,
    depthPercent,
    timeToDepthMs,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Comparison CTA click tracking — fired when user clicks CompareCTA on vehicle pages
interface CompareCTAClickEvent {
  event: 'compare_cta_clicked';
  vehicleName: string;
  rivalName: string;
  sourceSlug: string;
  timestamp: number;
}

export function trackCompareCTAClick(
  vehicleName: string,
  rivalName: string,
  sourceSlug: string,
): void {
  const event: CompareCTAClickEvent = {
    event: 'compare_cta_clicked',
    vehicleName,
    rivalName,
    sourceSlug,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Comment analytics tracking — submit + count viewed
interface CommentSubmittedEvent {
  event: 'comment_submitted';
  postId: string;
  timestamp: number;
}

export function trackCommentSubmitted(postId: string): void {
  const event: CommentSubmittedEvent = {
    event: 'comment_submitted',
    postId,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

interface CommentCountViewedEvent {
  event: 'comment_count_viewed';
  postId: string;
  commentCount: number;
  timestamp: number;
}

export function trackCommentCountViewed(postId: string, commentCount: number): void {
  const event: CommentCountViewedEvent = {
    event: 'comment_count_viewed',
    postId,
    commentCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Inline comment tracking — fired when user highlights text and submits inline comment
interface InlineCommentCreatedEvent {
  event: 'inline_comment_created';
  postId: string;
  highlightLength: number;
  timestamp: number;
}

export function trackInlineCommentCreated(postId: string, highlightLength: number): void {
  const event: InlineCommentCreatedEvent = {
    event: 'inline_comment_created',
    postId,
    highlightLength,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Garage add/join CTA click tracking — fired when user clicks Join on a garage
interface GarageAddClickEvent {
  event: 'garage_add_click';
  garageId: string;
  sourcePage: string;
  timestamp: number;
}

export function trackGarageAddClick(
  garageId: string,
  sourcePage: string,
): void {
  const event: GarageAddClickEvent = {
    event: 'garage_add_click',
    garageId,
    sourcePage,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Garage profile view tracking — fired when a garage page loads
interface GarageProfileViewEvent {
  event: 'garage_profile_view';
  garageSlug: string;
  vehicleCount: number;
  memberCount: number;
  timestamp: number;
}

export function trackGarageProfileView(
  garageSlug: string,
  vehicleCount: number,
  memberCount: number,
): void {
  const event: GarageProfileViewEvent = {
    event: 'garage_profile_view',
    garageSlug,
    vehicleCount,
    memberCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Pro upgrade funnel tracking — page view + scroll to CTA
export function trackProPageView(): void {
  const event = {
    event: 'pro_page_viewed' as const,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackProScrollToCTA(): void {
  const event = {
    event: 'pro_scroll_to_cta' as const,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-019: Pro checkout completion tracking — fired when Stripe redirects back with ?session=success
// ANALYTICS-022: attribution is read from sessionStorage on /pro entry and replayed
// here so the success event carries the same UTM context as checkout_start.
export function trackProCheckoutComplete(attribution?: ProCheckoutAttribution): void {
  // ANALYTICS-103: record funnel step for drop-off tracking
  recordProFunnelStep('checkout_complete');

  const event = {
    event: 'pro_checkout_complete' as const,
    trafficSource: attribution?.trafficSource ?? 'direct',
    ...(attribution?.utmMedium && { utmMedium: attribution.utmMedium }),
    ...(attribution?.utmCampaign && { utmCampaign: attribution.utmCampaign }),
    // ANALYTICS-102: carry waitlist source through checkout funnel
    ...(attribution?.waitlistSource && { waitlistSource: attribution.waitlistSource }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }

  // ANALYTICS-103: funnel complete — clear state so abandon doesn't fire
  clearProFunnelState();
}

// ANALYTICS-066: Pro checkout cancel — fired when Stripe redirects back with
// ?subscription=canceled (user abandoned checkout without completing payment).
export function trackProCheckoutCancel(): void {
  const event = {
    event: 'pro_checkout_cancel' as const,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Price drop email alert subscription — fired when user subscribes from a spec page
interface PriceAlertSubscribeEvent {
  event: 'price_alert_subscribe';
  vehicleSlug: string;
  brand?: string;
  timestamp: number;
}

export function trackPriceAlertSubscribe(vehicleSlug: string, brand?: string): void {
  const event: PriceAlertSubscribeEvent = {
    event: 'price_alert_subscribe',
    vehicleSlug,
    brand,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// MON-020: Pro subsidy alert — subscribe / unsubscribe
interface SubsidyAlertEvent {
  event: 'subsidy_alert_subscribe' | 'subsidy_alert_unsubscribe';
  vehicleSlug: string;
  alertTypes: string;
  timestamp: number;
}

export function trackSubsidyAlertSubscribe(vehicleSlug: string, alertTypes: string): void {
  const event: SubsidyAlertEvent = {
    event: 'subsidy_alert_subscribe',
    vehicleSlug,
    alertTypes,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch {}
}

export function trackSubsidyAlertUnsubscribe(vehicleSlug: string, alertTypes: string): void {
  const event: SubsidyAlertEvent = {
    event: 'subsidy_alert_unsubscribe',
    vehicleSlug,
    alertTypes,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch {}
}

// Vehicle launch alert subscription — fired when user subscribes from an upcoming vehicle page
interface VehicleAlertSubscribeEvent {
  event: 'vehicle_alert_subscribe';
  vehicleSlug: string;
  vehicleType: string;
  brand?: string;
  timestamp: number;
}

export function trackVehicleAlertSubscribe(vehicleSlug: string, vehicleType: string, brand?: string): void {
  const event: VehicleAlertSubscribeEvent = {
    event: 'vehicle_alert_subscribe',
    vehicleSlug,
    vehicleType,
    brand,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Reading time tracking — fired on PostDetail unmount
interface ReadingTimeEvent {
  event: 'reading_time';
  postId: string;
  readingTimeMs: number;
  timestamp: number;
}

export function trackReadingTime(postId: string, readingTimeMs: number): void {
  if (readingTimeMs < 2000) return; // ignore sub-2s bounces

  const event: ReadingTimeEvent = {
    event: 'reading_time',
    postId,
    readingTimeMs,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-003: Newsletter signup conversion funnel — impression → form_start → submit → success
type NewsletterFunnelStep = 'newsletter_impression' | 'newsletter_form_start' | 'newsletter_submit' | 'newsletter_success' | 'newsletter_cta_focused' | 'newsletter_cta_error' | 'newsletter_cta_abandoned';

interface NewsletterFunnelEvent {
  event: NewsletterFunnelStep;
  category: string;
  source: string;
  timestamp: number;
}

export function trackNewsletterFunnel(
  step: NewsletterFunnelStep,
  category: string,
  source: string,
): void {
  const event: NewsletterFunnelEvent = {
    event: step,
    category,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Bookmark rate per category tracking — fires on bookmark add/remove with category context
type BookmarkAction = 'bookmark_added' | 'bookmark_removed';

interface BookmarkActionEvent {
  event: BookmarkAction;
  postId: string;
  category: string;
  source: 'feed' | 'feed_grid' | 'detail';
  timestamp: number;
}

export function trackBookmarkAction(
  action: BookmarkAction,
  postId: string,
  category: string,
  source: 'feed' | 'feed_grid' | 'detail',
): void {
  const event: BookmarkActionEvent = {
    event: action,
    postId,
    category,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-027: Track bookmark interactions with vehicle metadata for watchlist baseline
interface BookmarkInteractionEvent {
  event: 'bookmark_click';
  article_id: string;
  fuel_type: string;
  body_type: string;
  price_band: string;
  source: 'feed' | 'feed_grid' | 'detail';
  action: 'bookmark_added' | 'bookmark_removed';
  timestamp: number;
}

export function trackBookmarkInteraction(
  action: 'bookmark_added' | 'bookmark_removed',
  articleId: string,
  tags: string[],
  category: string,
  vehicleTypes: string[],
  priceRange: string | null | undefined,
  source: 'feed' | 'feed_grid' | 'detail',
): void {
  const fuelTypes = ['ev', 'phev', 'hybrid', 'flex-fuel', 'petrol', 'diesel'];
  const fuelType = tags.find((t) => fuelTypes.includes(t.toLowerCase()))?.toLowerCase()
    || (category === 'ev' ? 'ev' : 'unknown');

  const event: BookmarkInteractionEvent = {
    event: 'bookmark_click',
    article_id: articleId,
    fuel_type: fuelType,
    body_type: vehicleTypes[0]?.toLowerCase() || 'unknown',
    price_band: priceRange || 'unknown',
    source,
    action,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Reaction tracking — COMM-003 (fire / like / interesting on feed cards + article pages)
interface PostReactionEvent {
  event: 'post_reaction';
  postId: string;
  reaction: 'fire' | 'like' | 'interesting' | null;
  surface: 'feed' | 'article';
  timestamp: number;
}

export function trackPostReaction(
  postId: string,
  reaction: 'fire' | 'like' | 'interesting' | null,
  surface: 'feed' | 'article'
): void {
  const event: PostReactionEvent = {
    event: 'post_reaction',
    postId,
    reaction,
    surface,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-007: AI summary quality flag rate tracking — reports flagSummary() results
// so ops can see % of articles with quality flags per source in production.
export type SummaryFlag = 'too_short' | 'html_artifact' | 'source_prefix' | 'generic_filler' | 'empty_short';

interface AISummaryFlagsEvent {
  event: 'ai_summary_quality_flags';
  postId: string;
  sourceId: string;
  flags: SummaryFlag[];
  flagCount: number;
  timestamp: number;
}

export function trackAISummaryFlags(
  flags: SummaryFlag[],
  postId: string,
  sourceId: string,
): void {
  if (flags.length === 0) return; // only emit when there are quality issues

  const event: AISummaryFlagsEvent = {
    event: 'ai_summary_quality_flags',
    postId,
    sourceId,
    flags,
    flagCount: flags.length,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-006: Upgrade prompt impression — tracks which surface triggered the upgrade CTA
// Distinct from pro_upsell_impression (feed-banner-specific); this covers all upgrade touchpoints.
export function trackUpgradePromptShown(
  trigger: 'sidebar' | 'feed_banner' | 'feed_top_banner' | 'post_detail' | 'comment_gate' | 'newsletter_modal'
): void {
  const event = {
    event: 'upgrade_prompt_shown' as const,
    trigger,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-020: Pro upgrade prompt dismiss — mirrors trackUpgradePromptShown so
// the shown→dismissed funnel can be sliced by trigger surface (alongside the
// existing shown→clicked path via trackProUpgradeClick).
export function trackUpgradePromptDismissed(
  trigger: 'sidebar' | 'feed_banner' | 'feed_top_banner' | 'post_detail' | 'comment_gate' | 'newsletter_modal'
): void {
  const event = {
    event: 'upgrade_prompt_dismissed' as const,
    trigger,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-006: Pro page exit without conversion — fires on navigation away from /pro
// without checkout start. viewDurationMs lets us see if users are reading or bouncing.
export function trackUpgradePageExited(viewDurationMs: number, scrolledToCta: boolean): void {
  const event = {
    event: 'upgrade_page_exited' as const,
    viewDurationMs,
    scrolledToCta,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-009: Pro feature engagement tracking — saved search usage + feature usage by tier
type ProFeature = 'saved_search' | 'price_alert' | 'weekly_digest' | 'ad_free';

interface ProFeatureUsedEvent {
  event: 'pro_feature_used';
  feature: ProFeature;
  action: string;
  isPro: boolean;
  timestamp: number;
}

export function trackProFeatureUsed(
  feature: ProFeature,
  action: string,
  isPro: boolean,
): void {
  const event: ProFeatureUsedEvent = {
    event: 'pro_feature_used',
    feature,
    action,
    isPro,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackSavedSearchUsed(
  action: 'viewed' | 'clicked' | 'created' | 'deleted' | 'toggled_notify',
  isPro: boolean,
  searchId?: string,
): void {
  const event = {
    event: 'saved_search_used' as const,
    action,
    isPro,
    searchId,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-010: Feed session duration tracking — postsViewed + durationMs per session
// ANALYTICS-011: Category engagement split — 2W vs 4W vs EV vs ADV breakdown per session
export type CategoryGroup = 'two-wheeler' | 'four-wheeler' | 'ev' | 'adv';

type PostCategory = 'cars' | 'motorcycles' | 'scooters' | 'ev' | 'adv';

const CATEGORY_TO_GROUP: Record<PostCategory, CategoryGroup> = {
  motorcycles: 'two-wheeler',
  scooters: 'two-wheeler',
  cars: 'four-wheeler',
  ev: 'ev',
  adv: 'adv',
};

export function categoriesToBreakdown(categories: PostCategory[]): Record<CategoryGroup, number> {
  const breakdown: Record<CategoryGroup, number> = { 'two-wheeler': 0, 'four-wheeler': 0, ev: 0, adv: 0 };
  for (const cat of categories) {
    const group = CATEGORY_TO_GROUP[cat];
    if (group) breakdown[group]++;
  }
  return breakdown;
}

interface FeedSessionEndEvent {
  event: 'feed_session_end';
  postsViewed: number;
  durationMs: number;
  reachedEnd: boolean;
  categoryBreakdown: Record<CategoryGroup, number>;
  timestamp: number;
}

export function trackFeedSessionEnd(
  postsViewed: number,
  durationMs: number,
  reachedEnd: boolean,
  categoryBreakdown?: Record<CategoryGroup, number>,
): void {
  if (durationMs < 1000) return; // ignore sub-1s bounces

  const event: FeedSessionEndEvent = {
    event: 'feed_session_end',
    postsViewed,
    durationMs,
    reachedEnd,
    categoryBreakdown: categoryBreakdown ?? { 'two-wheeler': 0, 'four-wheeler': 0, ev: 0, adv: 0 },
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-008: Hot Take poll vote tracking — tracks participation rate per article
export function trackPollVote(
  postId: string,
  voteType: 'hot' | 'cold',
): void {
  const event = {
    event: 'hot_take_vote' as const,
    postId,
    voteType,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event)
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-012: Track garage post upvote clicks
export function trackGaragePostUpvoteClick(
  postId: string,
  garageSlug: string,
): void {
  const event = {
    event: 'garage_post_upvote_click' as const,
    postId,
    garageSlug,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-041: Track garage post downvote clicks
export function trackGaragePostDownvoteClick(
  postId: string,
  garageSlug: string,
): void {
  const event = {
    event: 'garage_post_downvote_click' as const,
    postId,
    garageSlug,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-016: Track Pro page billing-cycle toggle clicks (Monthly vs Annual)
// Captures cycle exploration BEFORE checkout intent — distinct from
// trackProCheckoutStart which only fires on Get-Pro click.
export function trackProBillingCycleSelect(cycle: 'monthly' | 'annual'): void {
  const event = {
    event: 'pro_billing_cycle_select' as const,
    cycle,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// UI-037: Track weekly reading goal completion
export function trackWeeklyGoalComplete(goalTarget: number, articlesRead: number): void {
  const event = {
    event: 'weekly_goal_complete' as const,
    goalTarget,
    articlesRead,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-050: Track streak updates — fires when the daily streak is incremented
// after a successful POST /users/me/streak/record that changes the streak value.
export function trackStreakUpdated(streakLength: number, isNewRecord: boolean): void {
  const event = {
    event: 'streak_updated' as const,
    streakLength,
    isNewRecord,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-017: Track streak milestone toast interactions
// action: 'shown' on first render, 'dismissed' on user X click. milestone is the
// streak day count that triggered the toast (7/14/30/100).
export function trackStreakMilestoneToast(
  action: 'shown' | 'dismissed',
  milestone: number,
): void {
  const event = {
    event: 'streak_milestone_toast' as const,
    action,
    milestone,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-018: Track dwell time on /garage/:slug pages — fires once on
// navigation away (visibilitychange hidden, pagehide, or unmount). Sub-1s
// bounces are dropped to match feed_session_end behavior.
export function trackGaragePageDwell(
  garageSlug: string,
  durationMs: number,
  postsViewed: number,
): void {
  if (durationMs < 1000) return;

  const event = {
    event: 'garage_page_view' as const,
    garageSlug,
    durationMs,
    postsViewed,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-021: Save-to-Garage CTA funnel tracking — measures top-of-funnel
// registration intent for non-authenticated users.
//   click       → CTA tapped (auth or anon, source distinguishes feed vs detail)
//   auth_wall_hit → modal opened for an anon user and the sign-up gate rendered
// Pairs the existing trackGaragePostShare event (which fires only for the
// authenticated share flow). Splitting auth state into its own field lets the
// dashboards slice anon-click → anon-wall-hit conversion (the registration
// intent metric the ticket asks for).
type SaveToGarageCtaAction = 'click' | 'auth_wall_hit';

export function trackSaveToGarageCta(
  action: SaveToGarageCtaAction,
  postId: string,
  isAuthenticated: boolean,
  source: 'feed' | 'detail',
): void {
  const event = {
    event: 'save_to_garage_cta' as const,
    action,
    postId,
    isAuthenticated,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-026: Outbound link click tracking — fires when user clicks any
// external link from a feed card or article page. Captures postId, destination
// URL, and domain for affiliate attribution groundwork.
interface OutboundLinkClickEvent {
  event: 'link_click';
  postId: string;
  url: string;
  domain: string;
  source: 'feed' | 'detail';
  timestamp: number;
}

export function trackOutboundLinkClick(
  postId: string,
  url: string,
  source: 'feed' | 'detail',
): void {
  let domain = '';
  try {
    domain = new URL(url).hostname;
  } catch {
    domain = url;
  }

  const event: OutboundLinkClickEvent = {
    event: 'link_click',
    postId,
    url,
    domain,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// UI-042: 'Launching This Week' badge click — fires when a user clicks the amber
// this-week launch badge. Distinct from launching_soon_click (8-14d) and
// launching_today_click (same day) so dashboard can segment by urgency tier.
export function trackLaunchingThisWeekClick(
  postId: string,
  label: string,
  launchDate: string,
): void {
  const event = {
    event: 'launching_this_week_click' as const,
    postId,
    label,
    launchDate,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-027: 'Launching Soon' chip CTR tracking — fires when a user clicks
// the orange launch-calendar chip on a feed card. Pairs with UI-015 to measure
// chip discovery → engagement signal for upcoming launches.
export function trackLaunchingSoonClick(
  postId: string,
  label: string,
  launchDate: string,
): void {
  const event = {
    event: 'launching_soon_click' as const,
    postId,
    label,
    launchDate,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// UI-017: Track clicks on "Just Launched" badge
export function trackJustLaunchedClick(
  postId: string,
  label: string,
  launchDate: string,
): void {
  const event = {
    event: 'just_launched_click' as const,
    postId,
    label,
    launchDate,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// UI-038 / ANALYTICS-054: Track clicks on the red "LAUNCHING TODAY" hero badge.
// Always carries is_launch_day=true so downstream dashboards (ANALYTICS-054) can
// segment day-1 vs evergreen traffic without recomputing the calendar match
// server-side. Same shape as trackJustLaunchedClick + launching_soon_click for
// consistency with the rest of the launch-event family.
export function trackLaunchingTodayClick(
  postId: string,
  label: string,
  launchDate: string,
): void {
  const event = {
    event: 'launching_today_click' as const,
    postId,
    label,
    launchDate,
    is_launch_day: true as const,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-028: Price-intent signal tracking — captures click intent across
// price/specs/compare CTAs per vehicleSlug so MONETIZATION-002 ("Get Best
// Price" affiliate placement) can heat-map which vehicles drive the highest
// price-check intent. vehicleSlug is brand-prefixed (e.g. "tata-sierra-ev")
// so the same brand across multiple posts aggregates into a single ranking.
type PriceIntentAction = 'price_cta_click' | 'specs_click' | 'compare_click';

interface PriceIntentEvent {
  event: 'price_intent';
  action: PriceIntentAction;
  vehicleSlug: string;
  postId: string;
  source: 'feed' | 'detail';
  timestamp: number;
}

export function trackPriceIntent(
  action: PriceIntentAction,
  vehicleSlug: string,
  postId: string,
  source: 'feed' | 'detail',
): void {
  const event: PriceIntentEvent = {
    event: 'price_intent',
    action,
    vehicleSlug,
    postId,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-033: Track PWA install banner impression and outcome
export function trackPwaInstallImpression(): void {
  const event = {
    event: 'pwa_install_impression' as const,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackPwaInstallOutcome(
  outcome: 'install_click' | 'dismiss',
): void {
  const event = {
    event: 'pwa_install_outcome' as const,
    outcome,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-032: Track push notification permission state on app load
export function trackPushPermissionState(
  permissionState: 'granted' | 'denied' | 'default' | 'unsupported',
): void {
  const event = {
    event: 'push_permission_state' as const,
    permissionState,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-035: Invite-to-signup conversion tracking. Pairs with the existing
// utm_campaign=invite tag emitted by ShareBar (ANALYTICS-034) for invite-link
// shares. captureInviteAttribution() persists utm_campaign=invite into
// sessionStorage on first landing — register.tsx replays it on successful
// signup so the attribution survives navigation between landing → /auth/register.
const INVITE_ATTRIBUTION_STORAGE_KEY = 'mw_invite_attribution';

export interface InviteAttribution {
  utmSource?: string;
  referrerId?: string;
}

export function captureInviteAttribution(): InviteAttribution | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const isInvite = params.get('utm_campaign') === 'invite';
  if (isInvite) {
    const attribution: InviteAttribution = {};
    const utmSource = params.get('utm_source');
    const ref = params.get('ref') || params.get('referrer_id');
    if (utmSource) attribution.utmSource = utmSource;
    if (ref) attribution.referrerId = ref;
    try {
      window.sessionStorage.setItem(INVITE_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    } catch {
      // sessionStorage unavailable (private mode) — fall through, attribution stays in memory only
    }
    return attribution;
  }
  try {
    const stored = window.sessionStorage.getItem(INVITE_ATTRIBUTION_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as InviteAttribution;
  } catch {
    // ignore — attribution is best-effort
  }
  return null;
}

export function trackInviteConverted(userId: string, attribution?: InviteAttribution | null): void {
  if (!attribution) return; // only fire when an invite attribution exists
  const event = {
    event: 'invite_converted' as const,
    userId,
    ...(attribution.utmSource && { utmSource: attribution.utmSource }),
    ...(attribution.referrerId && { referrerId: attribution.referrerId }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }

  try {
    window.sessionStorage.removeItem(INVITE_ATTRIBUTION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ANALYTICS-040: Community funnel analytics — garage_view, garage_join,
// comment_attempt events to diagnose where the Community funnel breaks.
export function trackGarageListView(garageCount: number): void {
  const event = {
    event: 'garage_view' as const,
    garageCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageJoinClick(garageSlug: string, source: 'browse' | 'detail'): void {
  const event = {
    event: 'garage_join' as const,
    garageSlug,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackCommentAttempt(postId: string, isAuthenticated: boolean): void {
  const event = {
    event: 'comment_attempt' as const,
    postId,
    isAuthenticated,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-042: Garage activity funnel — created → post_added → invite_sent →
// invite_accepted. Pre-existing garage events (garage_share, garage_engagement,
// garage_join) cover sub-flows; these four give dashboards a single end-to-end
// conversion series for the Garage feature without joining heterogeneous names.
export function trackGarageCreated(garageSlug: string, category: string): void {
  const event = {
    event: 'garage_created' as const,
    garageSlug,
    category,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGaragePostAdded(postId: string, garageSlug: string): void {
  const event = {
    event: 'garage_post_added' as const,
    postId,
    garageSlug,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-079: Garage Save Funnel Tracking — click → success/abandoned flow.
// Complements existing trackGaragePostShare / trackSaveToGarageCta with explicit
// funnel stages and garage_count for engagement segmentation.
type GarageSaveSource = 'feed_card' | 'article_footer';

export function trackGarageSaveClick(
  articleId: string,
  source: GarageSaveSource,
  garageCount: number,
  sourceUrl: string = 'unknown',
  sourceName: string = 'unknown',
): void {
  const event = {
    event: 'garage_save_click' as const,
    articleId,
    source,
    garageCount,
    source_domain: extractDomain(sourceUrl),
    source_name: sourceName || 'unknown',
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageSaveSuccess(
  articleId: string,
  source: GarageSaveSource,
  garageCount: number,
  sourceUrl: string = 'unknown',
  sourceName: string = 'unknown',
): void {
  const event = {
    event: 'garage_save_success' as const,
    articleId,
    source,
    garageCount,
    source_domain: extractDomain(sourceUrl),
    source_name: sourceName || 'unknown',
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageSaveAbandoned(
  articleId: string,
  source: GarageSaveSource,
  garageCount: number,
  sourceUrl: string = 'unknown',
  sourceName: string = 'unknown',
): void {
  const event = {
    event: 'garage_save_abandoned' as const,
    articleId,
    source,
    garageCount,
    source_domain: extractDomain(sourceUrl),
    source_name: sourceName || 'unknown',
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

type GarageInviteMethod = 'copy_link' | 'native_share' | 'whatsapp' | 'twitter' | 'facebook' | 'linkedin' | 'native';

export function trackGarageInviteSent(garageSlug: string, method: GarageInviteMethod): void {
  const event = {
    event: 'garage_invite_sent' as const,
    garageSlug,
    method,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// `source` distinguishes invite-link conversions (utm/referrer attribution
// present at join time) from organic browse-and-join, so the dashboards can
// compute the invite-accept-rate separately from total-join-rate.
type GarageInviteAcceptSource = 'invite_link' | 'browse';

export function trackGarageInviteAccepted(garageSlug: string, source: GarageInviteAcceptSource): void {
  const event = {
    event: 'garage_invite_accepted' as const,
    garageSlug,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-056: Article age vs CTR tracking — fires on article click in the
// feed, correlating hours_since_publish with click-through so dashboards can
// reveal which categories decay fastest and whether breaking news is served
// before it goes stale. source_domain carries the publisher for per-source
// freshness benchmarking.
export function trackArticleAgeCtr(
  articleId: string,
  hoursSincePublish: number,
  category: string,
  sourceUrl: string,
  sourceName: string = 'unknown',
): void {
  const event = {
    event: 'article_age_ctr' as const,
    article_id: articleId,
    hours_since_publish: Math.round(hoursSincePublish * 10) / 10,
    category: category || 'unknown',
    source_domain: extractDomain(sourceUrl),
    source_name: sourceName || 'unknown',
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-057: Community Buzz engagement baseline — impression, click, and
// per-subreddit CTR so dashboards can measure whether community signals improve
// day-2/day-7 retention. Subreddit dimension lets us compare r/IndianBikes vs
// r/CarsIndia engagement independently.
export function trackCommunityBuzzImpression(): void {
  const event = {
    event: 'community_buzz_impression' as const,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackCommunityBuzzClick(subreddit: string, url: string): void {
  let domain = '';
  try {
    domain = new URL(url).hostname;
  } catch {
    domain = url;
  }

  const event = {
    event: 'community_buzz_click' as const,
    subreddit,
    url,
    domain,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-062: Garage CTA click-through funnel — 3 events to diagnose
// Community 🔴 lens root cause: discovery vs friction vs content quality.
// garage_badge_impression fires on viewport entry, garage_badge_click on tap,
// garage_join_click when user taps 'Join Garage' inside the garage view.
export function trackGarageBadgeImpression(postId: string, garageCount: number): void {
  const event = {
    event: 'garage_badge_impression' as const,
    postId,
    garageCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageBadgeClick(postId: string, garageCount: number): void {
  const event = {
    event: 'garage_badge_click' as const,
    postId,
    garageCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageJoinCta(garageSlug: string, source: 'feed_badge' | 'garage_page'): void {
  const event = {
    event: 'garage_join_click' as const,
    garageSlug,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-021: Mobile Garage Discovery Funnel — impression/click tracking for
// the community CTA card injected every 15 feed items. Position lets us compare
// slot depth performance; source distinguishes list vs card view.
export function trackGarageDiscoveryImpression(position: number, source: 'feed_list' | 'feed_grid'): void {
  const event = {
    event: 'garage_discovery_cta_impression' as const,
    position,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageDiscoveryClick(position: number, source: 'feed_list' | 'feed_grid'): void {
  const event = {
    event: 'garage_discovery_cta_click' as const,
    position,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-013: Track garage search queries
export function trackGarageSearch(
  query: string,
  resultCount: number,
  category: string,
): void {
  const event = {
    event: 'garage_search' as const,
    query,
    resultCount,
    category: category || 'all',
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-063: Affiliate link CTR baseline — article-to-buy-now funnel tracking.
// Captures article_id, vehicle_entity, cta_type, and destination_domain so
// monetization P&L can be sliced per article, per vehicle, per CTA flavor.
// ANALYTICS-064: Extended with articleSlug, partnerName, position for per-article
// revenue attribution — enables slicing affiliate clicks by content piece.
type AffiliatePosition = 'feed' | 'detail' | 'sticky' | 'inline';

interface AffiliateFunnelCTAEvent {
  event: 'affiliate_funnel_cta_click';
  articleId: string;
  articleSlug: string;
  vehicleEntity: string;
  ctaType: 'check_price' | 'book_test_drive' | 'buy_now' | 'insurance';
  partnerName: string;
  position: AffiliatePosition;
  destinationDomain: string;
  timestamp: number;
}

export function trackAffiliateFunnelCTA(
  articleId: string,
  vehicleEntity: string,
  ctaType: AffiliateFunnelCTAEvent['ctaType'],
  destinationUrl: string,
  partnerName: string = 'unknown',
  position: AffiliatePosition = 'detail',
  articleSlug: string = '',
): void {
  let destinationDomain: string;
  try {
    destinationDomain = new URL(destinationUrl).hostname;
  } catch {
    destinationDomain = 'unknown';
  }

  const event: AffiliateFunnelCTAEvent = {
    event: 'affiliate_funnel_cta_click',
    articleId,
    articleSlug: articleSlug || articleId,
    vehicleEntity,
    ctaType,
    partnerName,
    position,
    destinationDomain,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// UI-049: Topic/Brand follow from feed card
export function trackTopicFollow(
  type: 'brand' | 'topic',
  value: string,
  action: 'follow' | 'unfollow',
  source: 'feed_card' | 'tag_page' | 'sidebar' | 'search_result',
): void {
  const event = {
    event: 'topic_follow_toggle',
    type,
    value,
    action,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-067: Topic/Brand follow conversion funnel tracking
export function trackFollowConversion(
  type: 'brand' | 'topic',
  topicSlug: string,
  action: 'follow' | 'unfollow',
  source: 'feed_card' | 'explore_page' | 'tag_page' | 'sidebar' | 'search_result',
): void {
  const eventName = action === 'follow' ? 'follow_topic_click' : 'unfollow_topic_click';
  const event = {
    event: eventName,
    topicSlug,
    type,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

// ANALYTICS-068: Pro feature gate tracking — which gated features drive upgrades
// Fires on gate-hit (user encounters paywall), converted (user upgrades), dismissed (user closes)
type ProGateSource = 'feed' | 'article' | 'garage' | 'profile';

export function trackProGateHit(feature: string, source: ProGateSource): void {
  const event = {
    event: 'pro_gate_hit' as const,
    feature,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

export function trackProGateConverted(feature: string, timeToConvertMs: number): void {
  const event = {
    event: 'pro_gate_converted' as const,
    feature,
    timeToConvertMs,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

export function trackProGateDismissed(feature: string): void {
  const event = {
    event: 'pro_gate_dismissed' as const,
    feature,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

// ANALYTICS-068: Pro modal/overlay view tracking — fires when a Pro upgrade
// modal or overlay is displayed (distinct from banner impressions). Enables
// day-1 conversion funnel data when MON-021 Pro tier launches.
export type ProModalType = 'upgrade_gate' | 'trial_offer' | 'feature_gate' | 'upsell_overlay';

export function trackProModalView(
  modalType: ProModalType,
  triggeredBy: ProCtaLocation,
  feature?: string,
): void {
  const event = {
    event: 'pro_modal_view' as const,
    modalType,
    triggeredBy,
    ...(feature && { feature }),
    sessionAge: getSessionAgeMs(),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

// ANALYTICS-067: Milestone tracking — fires when user hits 1/5/10 follows
export function trackFollowCountMilestone(count: number): void {
  if (count !== 1 && count !== 5 && count !== 10) return;

  const key = `follow-milestone-${count}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // sessionStorage unavailable — still fire event
  }

  const event = {
    event: 'follow_count_milestone',
    count,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

// ANALYTICS-072: Social share button impression + click tracking
export function trackShareButtonImpression(postId: string, surface: 'detail_footer' | 'detail_sidebar' | 'detail_sticky' | 'whatsapp_cta' | 'whatsapp_page_cta' | 'feed_card' | 'feed_grid'): void {
  const event = {
    event: 'share_button_impression' as const,
    postId,
    surface,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackShareButtonClick(postId: string, channel: ShareChannel, surface: 'detail_footer' | 'detail_sidebar' | 'detail_sticky' | 'whatsapp_cta' | 'whatsapp_page_cta' | 'feed_card' | 'feed_grid'): void {
  const event = {
    event: 'share_button_click' as const,
    postId,
    channel,
    surface,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-071: Social proof pill impression + click tracking
export function trackSocialProofImpression(postId: string, brand: string, followerCount: number): void {
  const event = {
    event: 'garage_social_proof_impression' as const,
    postId,
    brand,
    followerCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-073 + COMM-014: Article reaction impression + click tracking
export type ReactionType = 'hot' | 'agree' | 'surprised' | 'thinking' | 'disagree';

export function trackReactionImpression(articleId: string): void {
  const event = {
    event: 'reaction_impression' as const,
    articleId,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackReactionClick(articleId: string, reactionType: ReactionType, totalCount: number): void {
  const event = {
    event: 'article_reaction_added' as const,
    articleId,
    reactionType,
    totalCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-101: Reaction session streak — fires on 3rd unique article reaction per session
const REACTION_STREAK_KEY = 'mw_reaction_session_count';
const REACTION_STREAK_THRESHOLD = 3;

export function trackReactionSessionStreak(articleId: string, reactionType: ReactionType): void {
  try {
    const raw = sessionStorage.getItem(REACTION_STREAK_KEY);
    const reacted: string[] = raw ? JSON.parse(raw) : [];

    if (reacted.includes(articleId)) return;
    reacted.push(articleId);
    sessionStorage.setItem(REACTION_STREAK_KEY, JSON.stringify(reacted));

    if (reacted.length !== REACTION_STREAK_THRESHOLD) return;

    const event = {
      event: 'reaction_session_streak' as const,
      sessionReactionCount: reacted.length,
      triggerArticleId: articleId,
      triggerReactionType: reactionType,
      timestamp: Date.now(),
    };

    if (import.meta.env.DEV) {
      console.log('[analytics]', event);
    }

    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-049: Community engagement funnel — trackReaction(articleId, reactionType, surface)
export function trackReaction(articleId: string, reactionType: ReactionType, surface: 'article' | 'feed'): void {
  const event = {
    event: 'community_reaction' as const,
    articleId,
    reactionType,
    surface,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackSocialProofClick(postId: string, brand: string, followerCount: number): void {
  const event = {
    event: 'garage_social_proof_click' as const,
    postId,
    brand,
    followerCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-076: Garage Discovery Funnel Tracking
// ---------------------------------------------------------------------------

export function trackGarageSpotlightImpression(garageId: string, garageName: string): void {
  const event = {
    event: 'garage_spotlight_impression' as const,
    garageId,
    garageName,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

export function trackGarageSpotlightClick(garageId: string, garageName: string): void {
  const event = {
    event: 'garage_spotlight_click' as const,
    garageId,
    garageName,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

export type GarageDirectorySource = 'sidebar_nav' | 'explore' | 'direct';

export function trackGarageDirectoryView(source: GarageDirectorySource): void {
  const event = {
    event: 'garage_directory_view' as const,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

export type GarageJoinSource = 'spotlight' | 'directory' | 'post_card' | 'garage_page';

export function trackGarageJoinFromSource(garageId: string, source: GarageJoinSource): void {
  const event = {
    event: 'garage_join_source' as const,
    garageId,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-074: Reaction badge impression on feed cards
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// UI-062: Check Best Price CTA Tracking
// ---------------------------------------------------------------------------

export function trackPriceCTAImpression(postId: string, category: string, affiliateSource?: string): void {
  const event = {
    event: 'price_cta_impression' as const,
    postId,
    category,
    ...(affiliateSource && { affiliateSource }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

export function trackPriceCTAClick(postId: string, category: string, targetUrl: string, affiliateSource?: string): void {
  const event = {
    event: 'price_cta_click' as const,
    postId,
    category,
    targetUrl,
    ...(affiliateSource && { affiliateSource }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-078: Check Best Price CTA Conversion Funnel
// ---------------------------------------------------------------------------

export function trackBestPriceCTAImpression(
  articleSlug: string,
  vehicleMake: string,
  vehicleModel: string,
  referrer: string,
  affiliateSource?: string,
): void {
  const event = {
    event: 'best_price_cta_impression' as const,
    articleSlug,
    vehicleMake,
    vehicleModel,
    referrer,
    ...(affiliateSource && { affiliateSource }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

export function trackBestPriceCTAClick(
  articleSlug: string,
  vehicleMake: string,
  vehicleModel: string,
  referrer: string,
  affiliateSource?: string,
): void {
  const event = {
    event: 'best_price_cta_click' as const,
    articleSlug,
    vehicleMake,
    vehicleModel,
    referrer,
    ...(affiliateSource && { affiliateSource }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-077: Related Tags Widget Click-Through Rate Tracking
// ---------------------------------------------------------------------------

export function trackRelatedTagImpression(sourceTag: string, tagCount: number): void {
  const event = {
    event: 'related_tag_impression' as const,
    sourceTag,
    tagCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

export function trackRelatedTagClick(
  sourceTag: string,
  clickedTag: string,
  position: number,
): void {
  const event = {
    event: 'related_tag_click' as const,
    sourceTag,
    clickedTag,
    position,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ANALYTICS-081: Finance Partner Click Tracking — EMI/Loan CTA Attribution
// Fires when a user clicks an outbound finance link (EMI calculator, loan,
// insurance partner). Captures partner + vehicle context so we can build a
// pitch deck for finance partner deals.

export type FinancePartner = 'bankbazaar' | 'hdfc' | 'icici' | 'policybazaar' | 'insurancedekho' | 'other';

interface FinanceClickEvent {
  event: 'finance_click';
  partner: FinancePartner;
  vehicleType: string;
  articleId: string;
  sourcePage: string;
  ctaType: 'emi' | 'insurance';
  timestamp: number;
}

function resolveFinancePartner(url: string): FinancePartner {
  try {
    const hostname = new URL(url).hostname;
    if (hostname.includes('bankbazaar')) return 'bankbazaar';
    if (hostname.includes('hdfc')) return 'hdfc';
    if (hostname.includes('icici')) return 'icici';
    if (hostname.includes('policybazaar')) return 'policybazaar';
    if (hostname.includes('insurancedekho')) return 'insurancedekho';
  } catch { /* invalid URL — fall through */ }
  return 'other';
}

export function trackFinanceClick(
  articleId: string,
  ctaType: 'emi' | 'insurance',
  targetUrl: string,
  vehicleType?: string,
): void {
  const sourcePage = typeof window !== 'undefined' ? window.location.pathname : '';
  const partner = resolveFinancePartner(targetUrl);
  const resolvedVehicleType = vehicleType ?? inferCategory(sourcePage) ?? 'unknown';

  const event: FinanceClickEvent = {
    event: 'finance_click',
    partner,
    vehicleType: resolvedVehicleType,
    articleId,
    sourcePage,
    ctaType,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------

// UI-064: Save count badge impression — fires once when badge enters viewport
export function trackSaveBadgeImpression(
  postId: string,
  saveCount: number,
): void {
  const event = {
    event: 'save_badge_impression' as const,
    postId,
    saveCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-084: Trending article click attribution — source=trending_now
export function trackTrendingArticleClick(
  articleId: string,
  category?: string,
  tag?: string,
): void {
  const event = {
    event: 'trending_article_click' as const,
    articleId,
    source: 'trending_now' as const,
    category: category ?? null,
    tag: tag ?? null,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------

// ANALYTICS-086: EvSalesLeaderboard widget interaction tracking — brand row
// clicks and CTA clicks so dashboards can see which EV brands users engage
// with and how many click through to the full market-share page.
export function trackEvLeaderboardClick(
  action: 'brand_click' | 'cta_click',
  brand?: string,
): void {
  const event = {
    event: 'widget_interaction' as const,
    widget: 'ev_sales_leaderboard' as const,
    action,
    ...(brand && { brand }),
    ...(action === 'cta_click' && { destination: '/ev/india-ev-two-wheeler-market-share' }),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-088: Compare Tool Engagement Tracking

export function trackCompareStarted(
  vehicleIds: string[],
  category: 'car' | 'bike',
  sourcePage: string,
): void {
  const event = {
    event: 'compare_started' as const,
    vehicleIds,
    category,
    sourcePage,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackCompareCompleted(
  vehicleIds: string[],
  category: 'car' | 'bike',
  sourcePage: string,
): void {
  const event = {
    event: 'compare_completed' as const,
    vehicleIds,
    category,
    sourcePage,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackCompareShared(
  vehicleIds: string[],
  category: 'car' | 'bike',
  sourcePage: string,
): void {
  const event = {
    event: 'compare_shared' as const,
    vehicleIds,
    category,
    sourcePage,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackCompareAbandoned(
  vehicleIds: string[],
  category: 'car' | 'bike',
  sourcePage: string,
  dwellMs: number,
): void {
  const event = {
    event: 'compare_abandoned' as const,
    vehicleIds,
    category,
    sourcePage,
    dwellMs,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-030: Compare Funnel Baseline — Pairs, Time-on-Page, Drop-off

export function trackComparePairViewed(
  vehicleIds: string[],
  category: 'car' | 'bike',
  sourcePage: string,
  pairLabel: string,
): void {
  const event = {
    event: 'compare_pair_viewed' as const,
    vehicleIds,
    pairCount: vehicleIds.length,
    pairLabel,
    category,
    sourcePage,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackCompareTimeSpent(
  vehicleIds: string[],
  category: 'car' | 'bike',
  sourcePage: string,
  dwellMs: number,
  reachedVerdict: boolean,
): void {
  const event = {
    event: 'compare_time_spent' as const,
    vehicleIds,
    pairLabel: vehicleIds.join('-vs-'),
    category,
    sourcePage,
    dwellMs,
    reachedVerdict,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackReactionBadgeImpression(
  articleSlug: string,
  topEmoji: 'fire' | 'like' | 'interesting',
  totalCount: number,
): void {
  const event = {
    event: 'reaction_badge_impression' as const,
    articleSlug,
    topEmoji,
    totalCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-089: Community Health KPI Tracking
// ---------------------------------------------------------------------------

export type CommentPostedSource = 'article' | 'garage';

export function trackCommentPosted(
  articleId: string,
  garageSlug?: string,
): void {
  const event = {
    event: 'comment_posted' as const,
    articleId,
    ...(garageSlug && { garageSlug }),
    source: (garageSlug ? 'garage' : 'article') as CommentPostedSource,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export type GarageFollowedSource = 'feed' | 'profile' | 'search' | 'leaderboard' | 'badge';

export function trackGarageFollowed(
  garageSlug: string,
  source: GarageFollowedSource,
): void {
  const event = {
    event: 'garage_followed' as const,
    garageSlug,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-093: Garage Follow/Unfollow & View Tracking Events
// Completes the community tracking funnel so we can measure viral loops
// and A/B test the Garage Activity Pulse Widget (UI-072).
// garage_followed already exists above (ANALYTICS-089); adding unfollowed,
// profile_viewed, and activity_widget_click here.
// ---------------------------------------------------------------------------

export function trackGarageUnfollowed(
  garageSlug: string,
): void {
  const event = {
    event: 'garage_unfollowed' as const,
    garageSlug,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageProfileViewed(
  garageSlug: string,
  viewerId: string | null,
  referrer: string,
): void {
  const event = {
    event: 'garage_profile_viewed' as const,
    garageSlug,
    viewerId,
    referrer,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export type GarageActivityWidgetEvent = 'view_garage' | 'join_garage' | 'view_post';

export function trackGarageActivityWidgetClick(
  eventType: GarageActivityWidgetEvent,
  targetGarageId: string,
): void {
  const event = {
    event: 'garage_activity_widget_click' as const,
    event_type: eventType,
    target_garage_id: targetGarageId,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-090: YouTube Video Card Play-through Tracking
// ---------------------------------------------------------------------------

export type VideoCardSurface = 'feed_list' | 'feed_grid' | 'detail' | 'search';

export function trackVideoCardImpression(
  postId: string,
  category: string,
  duration: number | null,
  surface: VideoCardSurface = 'feed_list',
): void {
  const event = {
    event: 'video_card_impression' as const,
    postId,
    category,
    duration: duration ?? null,
    surface,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackVideoCardClick(
  postId: string,
  category: string,
  duration: number | null,
  surface: VideoCardSurface = 'feed_list',
): void {
  const event = {
    event: 'video_card_click' as const,
    postId,
    category,
    duration: duration ?? null,
    surface,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackVideoPlayStarted(
  postId: string,
  category: string,
  duration: number | null,
  surface: VideoCardSurface = 'feed_list',
): void {
  const event = {
    event: 'video_play_started' as const,
    postId,
    category,
    duration: duration ?? null,
    surface,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-091: Sponsored Feed Card Impression/Click/Skip Tracking
// Pre-wired for MON-001 (dealer/brand native ad slots). Events fire when
// sponsored cards are rendered, clicked, or scrolled past so day-1 ROI data
// is available for OEM sales conversations.
// ---------------------------------------------------------------------------

export type SponsoredCtaType = 'see_price' | 'book_test_drive';

export function trackSponsoredCardImpression(
  postId: string,
  position: number,
  brand: string,
  vehicleCategory: string,
): void {
  const event = {
    event: 'sponsored_card_impression' as const,
    postId,
    position,
    brand,
    vehicle_category: vehicleCategory,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackSponsoredCardClick(
  postId: string,
  ctaType: SponsoredCtaType,
  brand: string,
  vehicleCategory: string,
): void {
  const event = {
    event: 'sponsored_card_click' as const,
    postId,
    cta_type: ctaType,
    brand,
    vehicle_category: vehicleCategory,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackSponsoredCardSkip(
  postId: string,
  position: number,
  brand: string,
  vehicleCategory: string,
): void {
  const event = {
    event: 'sponsored_card_skip' as const,
    postId,
    position,
    brand,
    vehicle_category: vehicleCategory,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-094: Pro upgrade CTA click — full funnel instrumentation.
// Fires on every button/link that leads to Pro subscription checkout.
type ProUpgradeCTASource = 'banner' | 'feed' | 'article' | 'garage' | 'profile' | 'sidebar' | 'bookmarks';

interface ProUpgradeCTAClickEvent {
  event: 'pro_upgrade_cta_click';
  source: ProUpgradeCTASource;
  userId: string | null;
  articleId: string | null;
  ctaVariant: string | null;
  timestamp: number;
}

export function trackProUpgradeCTAClick(
  source: ProUpgradeCTASource,
  userId: string | null = null,
  articleId: string | null = null,
  ctaVariant: string | null = null,
): void {
  const event: ProUpgradeCTAClickEvent = {
    event: 'pro_upgrade_cta_click',
    source,
    userId,
    articleId,
    ctaVariant,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-095: Pro Digest waitlist email capture — top-of-funnel demand
// signal for the AI Daily Digest feature (competitive response to daily.dev
// Presidential Briefing). Fires when a user submits their email on the
// ProDigestWaitlist widget.
export type WaitlistSource = 'homepage' | 'sidebar' | 'feed' | 'article' | 'extension';

interface WaitlistEmailCapturedEvent {
  event: 'waitlist_email_captured';
  source: WaitlistSource;
  email: string;
  timestamp: number;
}

// ANALYTICS-102: Waitlist → Payment funnel sessionStorage key.
// Persisted on email capture so downstream checkout events carry waitlist
// attribution even across page navigations within the session.
const WAITLIST_SOURCE_KEY = 'mw_waitlist_source';

/** Read waitlist source from sessionStorage (or null if none). */
export function getWaitlistSource(): WaitlistSource | null {
  try {
    const val = typeof window !== 'undefined'
      ? window.sessionStorage.getItem(WAITLIST_SOURCE_KEY)
      : null;
    return val as WaitlistSource | null;
  } catch {
    return null;
  }
}

export function trackWaitlistEmailCaptured(
  source: WaitlistSource,
  email: string,
): void {
  // ANALYTICS-103: record funnel step for drop-off tracking
  recordProFunnelStep('email_captured');

  // ANALYTICS-102: persist source so checkout events downstream carry
  // waitlist attribution for the rest of this session.
  try {
    window.sessionStorage.setItem(WAITLIST_SOURCE_KEY, source);
  } catch { /* sessionStorage blocked — non-fatal */ }

  const event: WaitlistEmailCapturedEvent = {
    event: 'waitlist_email_captured',
    source,
    email,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-102: Waitlist → Pro upgrade bridge event — fires when a user who
// previously joined the waitlist visits /pro. Lets dashboards measure how many
// waitlist sign-ups convert to upgrade intent (impression → email → upgrade page).
export function trackWaitlistToProUpgrade(waitlistSource: WaitlistSource): void {
  // ANALYTICS-103: record funnel step for drop-off tracking
  recordProFunnelStep('pro_page_visit');

  const event = {
    event: 'waitlist_to_pro_upgrade' as const,
    waitlistSource,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-096: Pro waitlist funnel tracking — impression + click events
// to measure conversion: impression → click → email captured.
interface WaitlistWidgetImpressionEvent {
  event: 'pro_waitlist_widget_impression';
  source: WaitlistSource;
  timestamp: number;
}

interface WaitlistWidgetClickEvent {
  event: 'pro_waitlist_widget_click';
  source: WaitlistSource;
  timestamp: number;
}

export function trackProWaitlistWidgetImpression(
  source: WaitlistSource,
): void {
  // ANALYTICS-103: record funnel step for drop-off tracking
  recordProFunnelStep('waitlist_impression');

  const event: WaitlistWidgetImpressionEvent = {
    event: 'pro_waitlist_widget_impression',
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackProWaitlistWidgetClick(
  source: WaitlistSource,
): void {
  // ANALYTICS-103: record funnel step for drop-off tracking
  recordProFunnelStep('waitlist_click');

  const event: WaitlistWidgetClickEvent = {
    event: 'pro_waitlist_widget_click',
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-098: Garage Social Share Event Tracking
// Tracks when a user clicks a share button on a Garage page. Captures
// platform dimension (whatsapp / twitter / link_copy) plus vehicle count
// so we can measure organic viral sharing behavior per garage.
// ---------------------------------------------------------------------------

export type GarageSharePlatform = 'whatsapp' | 'twitter' | 'link_copy' | 'facebook' | 'linkedin' | 'copy_link' | 'native';

export function trackShareGarageClick(
  garageId: string,
  platform: GarageSharePlatform,
  garageVehicleCount: number,
): void {
  const event = {
    event: 'share_garage_click' as const,
    garage_id: garageId,
    platform,
    garage_vehicle_count: garageVehicleCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-099: Article Save/Bookmark Funnel — Event Tracking
// Richer save/unsave events with source_domain + position_in_feed for funnel
// analysis. Complements existing trackBookmarkAction with attribution data.
// ---------------------------------------------------------------------------

export function trackArticleSaved(
  articleId: string,
  sourceDomain: string,
  category: string,
  positionInFeed: number,
): void {
  const event = {
    event: 'article_saved' as const,
    article_id: articleId,
    source_domain: sourceDomain,
    category,
    position_in_feed: positionInFeed,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackArticleUnsaved(
  articleId: string,
  sourceDomain: string,
): void {
  const event = {
    event: 'article_unsaved' as const,
    article_id: articleId,
    source_domain: sourceDomain,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackSavesBadgeViewed(
  articleId: string,
  saveCountShown: number,
): void {
  const event = {
    event: 'saves_badge_viewed' as const,
    article_id: articleId,
    save_count_shown: saveCountShown,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-100: Topic Tag Click + Trending Sidebar Tracking
// ---------------------------------------------------------------------------

export function trackTagClicked(
  tagName: string,
  articleId: string,
  source: 'article_card' | 'sidebar',
): void {
  const event = {
    event: 'tag_clicked' as const,
    tag_name: tagName,
    article_id: articleId,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackTrendingSidebarImpression(
  tagsShown: string[],
  section: 'trending_now' | 'trending_week',
): void {
  const event = {
    event: 'trending_sidebar_impression' as const,
    tags_shown: tagsShown,
    section,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackTrendingSidebarClick(
  tagName: string,
  rank: number,
  section: 'trending_now' | 'trending_week',
): void {
  const event = {
    event: 'trending_sidebar_click' as const,
    tag_name: tagName,
    rank,
    section,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-103: ProDigest Funnel Drop-Off Tracking
// Records timestamps at each funnel step in sessionStorage so drop-off
// points and time-on-step can be measured. Fires pro_funnel_abandon via
// visibilitychange when a user leaves mid-funnel without completing.
// ---------------------------------------------------------------------------

const PRO_FUNNEL_STEPS = [
  'waitlist_impression',
  'waitlist_click',
  'email_captured',
  'pro_page_visit',
  'upgrade_started',
  'checkout_start',
  'checkout_complete',
] as const;

export type ProFunnelStep = (typeof PRO_FUNNEL_STEPS)[number];

const PRO_FUNNEL_KEY = 'mw_pro_funnel';

interface ProFunnelState {
  step: ProFunnelStep;
  ts: number;
  entryStep: ProFunnelStep;
  entryTs: number;
}

/** Record a funnel step; returns time since last step (or null if first). */
function recordProFunnelStep(step: ProFunnelStep): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const prev = getProFunnelState();
    const now = Date.now();
    const elapsed = prev ? now - prev.ts : null;

    const state: ProFunnelState = {
      step,
      ts: now,
      entryStep: prev?.entryStep ?? step,
      entryTs: prev?.entryTs ?? now,
    };
    window.sessionStorage.setItem(PRO_FUNNEL_KEY, JSON.stringify(state));
    return elapsed;
  } catch {
    return null;
  }
}

export function getProFunnelState(): ProFunnelState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PRO_FUNNEL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearProFunnelState(): void {
  try { window.sessionStorage.removeItem(PRO_FUNNEL_KEY); } catch { /* non-fatal */ }
}

// Fire abandon event when user leaves mid-funnel
function fireProFunnelAbandon(): void {
  const state = getProFunnelState();
  if (!state || state.step === 'checkout_complete') return;

  const now = Date.now();
  const event = {
    event: 'pro_funnel_abandon' as const,
    lastStep: state.step,
    timeOnStepMs: now - state.ts,
    totalFunnelTimeMs: now - state.entryTs,
    entryStep: state.entryStep,
    timestamp: now,
  };

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore
  }
}

// ANALYTICS-104: AI Summary Expansion CTR — fires when user expands a truncated
// AI summary on a feed card. Baseline data for Signal Shield investment decision.
interface AISummaryExpandedEvent {
  event: 'ai_summary_expanded';
  postId: string;
  category: string;
  source: 'feed' | 'article';
  position: number;
  timestamp: number;
}

export function trackAISummaryExpanded(
  postId: string,
  category: string,
  source: 'feed' | 'article',
  position: number,
): void {
  const event: AISummaryExpandedEvent = {
    event: 'ai_summary_expanded',
    postId,
    category,
    source,
    position,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-106: AI-011 TL;DR card impression — tracks when TL;DR card is visible on feed
export function trackTLDRCardImpression(
  postId: string,
  category: string,
  hasEntities: boolean,
  position: number,
): void {
  const event = {
    event: 'tldr_card_impression',
    postId,
    category,
    hasEntities,
    position,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-105: Source quality score badge impression
export function trackSourceQualityBadgeImpression(
  postId: string,
  sourceId: string,
  sourceName: string,
  reputationScore: number,
  tier: 'trusted' | 'low',
  position: number,
): void {
  const event = {
    event: 'source_quality_badge_impression',
    postId,
    sourceId,
    sourceName,
    reputationScore,
    tier,
    position,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-106: Pro Conversion Funnel Events — five canonical events that form
// a strict linear funnel: impression → click → checkout open → success/abandon.
// These complement (not replace) the existing per-surface trackers above; the
// funnel events use a shared `source` enum so dashboards can measure conversion
// drop-off from a single event stream without joining heterogeneous names.
export type ProFunnelSource = 'feed_upsell' | 'sidebar' | 'settings' | 'feature_gate' | 'article_footer' | 'pro_page';

export function trackProPaywallImpression(source: ProFunnelSource, featureName?: string): void {
  const event = {
    event: 'pro_paywall_impression' as const,
    source,
    ...(featureName && { featureName }),
    sessionAge: getSessionAgeMs(),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackProUpgradeButtonClick(source: ProFunnelSource): void {
  const event = {
    event: 'pro_upgrade_button_click' as const,
    source,
    sessionAge: getSessionAgeMs(),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackProCheckoutOpen(source: ProFunnelSource): void {
  const event = {
    event: 'pro_checkout_open' as const,
    source,
    sessionAge: getSessionAgeMs(),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackProCheckoutSuccess(): void {
  const event = {
    event: 'pro_checkout_success' as const,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackProCheckoutAbandoned(): void {
  const event = {
    event: 'pro_checkout_abandoned' as const,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// MON-010: Pro sticky trial bar dismissed — tracks when a non-Pro user
// closes the sticky bottom bar so we can measure dismiss rate.
export function trackProStickyBarDismissed(): void {
  const event = {
    event: 'pro_sticky_bar_dismissed' as const,
    sessionAgeMs: getSessionAgeMs(),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-107: Session category affinity — fires when a user views 3+ articles
// in the same category within a single session. Downstream can auto-promote
// the category into user preferences for feed personalization.
export function trackSessionCategoryAffinity(category: string, count: number): void {
  const event = {
    event: 'session_category_affinity' as const,
    category,
    count,
    sessionAgeMs: getSessionAgeMs(),
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-012: poll_share — user shares poll results
export function trackPollShare(
  postId: string,
  voteType: 'hot' | 'cold',
  method: 'clipboard' | 'native',
): void {
  const event = {
    event: 'poll_share' as const,
    postId,
    voteType,
    method,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// UI-093: garage_pulse_impression — user sees the Garage Pulse widget
export function trackGaragePulseImpression(
  activeGarages: number,
  topGarageName: string,
): void {
  const event = {
    event: 'garage_pulse_impression' as const,
    activeGarages,
    topGarageName,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// Analytics-013: launch_shelf_click — user clicks a launch item in the LaunchShelf
export function trackLaunchShelfClick(
  launchName: string,
  launchPath: string,
  vehicleType: 'ev' | 'bike' | 'car',
): void {
  const event = {
    event: 'launch_shelf_click' as const,
    launchName,
    launchPath,
    vehicleType,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// UI-095: launch_shelf_book_now — user clicks "Book Now" CTA on a LaunchShelf item
export function trackLaunchShelfBookNow(
  launchName: string,
  brand: string,
  destinationUrl: string,
  vehicleType: 'ev' | 'bike' | 'car',
): void {
  const event = {
    event: 'launch_shelf_book_now' as const,
    launchName,
    brand,
    destinationUrl,
    vehicleType,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// MONET-001: Insurance affiliate CTA tracking
export function trackInsuranceCtaClick(
  postId: string,
  partner: string,
  destinationUrl: string,
  vehicleCategory: string,
  vehicleName: string,
): void {
  const event = {
    event: 'insurance_cta_click' as const,
    postId,
    partner,
    destinationUrl,
    vehicleCategory,
    vehicleName,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackInsuranceCtaImpression(
  postId: string,
  vehicleCategory: string,
  vehicleName: string,
): void {
  const event = {
    event: 'insurance_cta_impression' as const,
    postId,
    vehicleCategory,
    vehicleName,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// MONET-002: Car loan EMI affiliate CTA analytics
export function trackCarLoanCtaClick(
  postId: string,
  partner: string,
  destinationUrl: string,
  vehicleCategory: string,
  vehicleName: string,
): void {
  const event = {
    event: 'car_loan_cta_click' as const,
    postId,
    partner,
    destinationUrl,
    vehicleCategory,
    vehicleName,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackCarLoanCtaImpression(
  postId: string,
  vehicleCategory: string,
  vehicleName: string,
): void {
  const event = {
    event: 'car_loan_cta_impression' as const,
    postId,
    vehicleCategory,
    vehicleName,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// MONET-004: Dealer price lead-gen CTA analytics
export function trackDealerPriceCtaClick(
  postId: string,
  partner: string,
  destinationUrl: string,
  vehicleCategory: string,
  vehicleName: string,
): void {
  const event = {
    event: 'dealer_price_cta_click' as const,
    postId,
    partner,
    destinationUrl,
    vehicleCategory,
    vehicleName,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {}
}

export function trackDealerPriceCtaImpression(
  postId: string,
  vehicleCategory: string,
  vehicleName: string,
): void {
  const event = {
    event: 'dealer_price_cta_impression' as const,
    postId,
    vehicleCategory,
    vehicleName,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {}
}

// MONET-005: OEM Native Sponsored Content analytics
export function trackOEMSponsoredCtaClick(
  postId: string,
  oemSlug: string,
  oemName: string,
  destinationUrl: string,
  category: string,
): void {
  const event = {
    event: 'oem_sponsored_cta_click' as const,
    postId,
    oemSlug,
    oemName,
    destinationUrl,
    category,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {}
}

export function trackOEMSponsoredCtaImpression(
  postId: string,
  oemSlug: string,
  oemName: string,
  category: string,
): void {
  const event = {
    event: 'oem_sponsored_cta_impression' as const,
    postId,
    oemSlug,
    oemName,
    category,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {}
}

// COMM-001: Compare widget analytics (in-article comparison suggestions)
export function trackCompareWidgetImpression(postId: string, matchCount: number): void {
  const event = {
    event: 'compare_widget_impression' as const,
    postId,
    matchCount,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch {}
}

export function trackCompareWidgetClick(postId: string, comparePath: string, compareTitle: string): void {
  const event = {
    event: 'compare_widget_click' as const,
    postId,
    comparePath,
    compareTitle,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch {}
}

// ANALYTICS-014: garage_post_viewed — tracks individual garage post impressions
export function trackGaragePostView(
  postId: string,
  garageSlug: string,
): void {
  const event = {
    event: 'garage_post_viewed' as const,
    postId,
    garageSlug,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    // Fire analytics event
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
    // Increment view count on the post
    navigator.sendBeacon?.(`/api/garages/posts/${postId}/view`);
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-016: WhatsApp Share Attribution — Inbound Share Arrival Tracking
// Fires when a user arrives via a shared link (detected by utm_source +
// utm_medium=share). Captures the referring channel and landing post so we
// can compute k-factor (viral coefficient) per share channel.
// Once per session: stores a sessionStorage flag to avoid double-counting
// on SPA navigations within the same shared-link session.
// ---------------------------------------------------------------------------

const SHARE_ARRIVAL_KEY = 'mw_share_arrival_fired';
const SHARE_ARRIVAL_CHANNEL_KEY = 'mw_share_arrival_channel';

export type ShareArrivalChannel = 'whatsapp' | 'twitter' | 'facebook' | 'linkedin' | 'copy_link' | 'native' | string;

/** GROW-001: Read the share arrival channel from sessionStorage (set by trackShareArrival). */
export function getShareArrivalChannel(): ShareArrivalChannel | null {
  try {
    return sessionStorage.getItem(SHARE_ARRIVAL_CHANNEL_KEY) as ShareArrivalChannel | null;
  } catch {
    return null;
  }
}

/** GROW-001: Clear share arrival channel after banner is dismissed. */
export function clearShareArrivalChannel(): void {
  try {
    sessionStorage.removeItem(SHARE_ARRIVAL_CHANNEL_KEY);
  } catch {
    // ignore
  }
}

export function trackShareArrival(
  channel: ShareArrivalChannel,
  landingPath: string,
  postId?: string,
  campaign?: string,
): void {
  // Only fire once per session
  try {
    if (sessionStorage.getItem(SHARE_ARRIVAL_KEY)) return;
    sessionStorage.setItem(SHARE_ARRIVAL_KEY, '1');
    // GROW-001: Store channel so ShareArrivalBanner can display it
    sessionStorage.setItem(SHARE_ARRIVAL_CHANNEL_KEY, channel);
  } catch {
    // Private browsing — proceed but accept possible double-count
  }

  const event = {
    event: 'share_arrival' as const,
    channel,
    landingPath,
    ...(postId && { postId }),
    ...(campaign && { campaign }),
    referrer: document.referrer || undefined,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics] share_arrival', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

/**
 * ANALYTICS-016: Detect UTM share params on current URL and fire share_arrival.
 * Call once from layout/root component on initial load.
 * Strips UTM params from URL after capturing to keep URLs clean.
 */
export function detectAndTrackShareArrival(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');

  // Only track arrivals via share links (utm_medium=share)
  if (!utmSource || utmMedium !== 'share') return;

  // Extract post ID from path if on a post page (e.g. /post/{uuid})
  const postMatch = window.location.pathname.match(/^\/post\/([a-f0-9-]+)/i);
  const postId = postMatch?.[1];
  const campaign = params.get('utm_campaign') || undefined;

  trackShareArrival(utmSource, window.location.pathname, postId, campaign);

  // Clean UTM params from URL without triggering navigation
  params.delete('utm_source');
  params.delete('utm_medium');
  params.delete('utm_campaign');
  const cleanUrl = params.toString()
    ? `${window.location.pathname}?${params.toString()}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`;
  window.history.replaceState(null, '', cleanUrl);
}

/** GROW-001: Track share arrival banner interactions. */
export function trackShareBannerEvent(action: 'impression' | 'dismiss' | 'cta_click', channel: string): void {
  const event = {
    event: 'share_banner' as const,
    action,
    channel,
    timestamp: Date.now(),
  };
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* analytics should never break UX */ }
}

// ANALYTICS-017: Day-1 / Day-7 / Day-30 Return Visit Cohort Events
// Fires once per milestone when a returning visitor crosses the threshold since
// their very first visit. Milestones stored in localStorage to deduplicate.
const COHORT_MILESTONES = [1, 7, 30] as const;
const FIRST_VISIT_KEY = 'mw_first_visit_ts';
const COHORT_FIRED_KEY = 'mw_cohort_fired';

function fireReturnVisitCohortEvents(): void {
  if (typeof window === 'undefined') return;
  try {
    const now = Date.now();
    let firstVisit = Number(localStorage.getItem(FIRST_VISIT_KEY));
    if (!(firstVisit > 0)) {
      // First ever visit — record and exit (no milestone can fire on day 0)
      localStorage.setItem(FIRST_VISIT_KEY, String(now));
      return;
    }

    const daysSinceFirst = Math.floor((now - firstVisit) / (1000 * 60 * 60 * 24));
    if (daysSinceFirst < 1) return; // still day 0

    const firedRaw = localStorage.getItem(COHORT_FIRED_KEY);
    const fired = new Set<number>(firedRaw ? JSON.parse(firedRaw) : []);

    for (const milestone of COHORT_MILESTONES) {
      if (daysSinceFirst >= milestone && !fired.has(milestone)) {
        fired.add(milestone);
        const event = {
          event: `return_visit_day_${milestone}` as const,
          daysSinceFirst,
          firstVisitTs: firstVisit,
          timestamp: now,
        };
        navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
      }
    }

    localStorage.setItem(COHORT_FIRED_KEY, JSON.stringify([...fired]));
  } catch { /* localStorage may be blocked — silently ignore */ }
}

if (typeof window !== 'undefined') {
  fireReturnVisitCohortEvents();
}

// visibilitychange is more reliable than beforeunload on mobile
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      fireProFunnelAbandon();
    }
  });
}

// ANALYTICS-018: Push notification opt-in rate tracking.
// Fires when user toggles push notifications on/off in settings, and when
// they toggle individual push categories. Complements ANALYTICS-032 which
// tracks browser-level permission state on load.
export function trackPushOptIn(enabled: boolean): void {
  const event = {
    event: 'push_opt_in_toggle' as const,
    enabled,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore */ }
}

export function trackPushCategoryToggle(
  category: string,
  enabled: boolean,
): void {
  const event = {
    event: 'push_category_toggle' as const,
    category,
    enabled,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore */ }
}

export function trackPushFrequencyChange(
  category: string,
  frequency: string,
): void {
  const event = {
    event: 'push_frequency_change' as const,
    category,
    frequency,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore */ }
}

// ANALYTICS-025: Track related article click-through rate — baseline before
// ENGAGE-005 (Related Articles Carousel) ships. Captures source/target article
// IDs and the surface (sidebar vs bottom grid) so we can measure navigation
// patterns between articles and quantify the carousel's impact post-ship.
export function trackRelatedArticleClick(
  sourceArticleId: string,
  targetArticleId: string,
  surface: 'sidebar' | 'bottom_grid',
  position: number,
  category?: string,
): void {
  const event = {
    event: 'related_article_click' as const,
    sourceArticleId,
    targetArticleId,
    surface,
    position,
    ...(category && { category }),
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore */ }
}

// ANALYTICS-029: Unified monetization CTA tracking — baseline before any new
// affiliate/dealer CTA ships. Provides a single event pair (impression + click)
// with a `ctaType` discriminator so all monetization surfaces can be queried,
// compared, and funnelled together regardless of which CTA widget fires them.
export type MonetizationCtaType =
  | 'insurance'
  | 'car_loan'
  | 'dealer_price'
  | 'oem_sponsored'
  | 'test_drive'
  | 'affiliate_recommendation'
  | 'price_check'
  | 'best_price'
  | 'ev_insurance'
  | 'book_test_drive'
  | 'price_hike_banner'
  | 'price_hike_finance'
  | 'used_car_cpl';

export function trackMonetizationCtaImpression(
  ctaType: MonetizationCtaType,
  postId: string,
  vehicleCategory: string,
  vehicleName: string,
  partner?: string,
  surface?: string,
): void {
  const event = {
    event: 'monetization_cta_impression' as const,
    ctaType,
    postId,
    vehicleCategory,
    vehicleName,
    ...(partner && { partner }),
    ...(surface && { surface }),
    sessionAgeMs: getSessionAgeMs(),
    ...(getUtmAttribution() || {}),
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore — analytics should never break UX */ }
}

export function trackMonetizationCtaClick(
  ctaType: MonetizationCtaType,
  postId: string,
  vehicleCategory: string,
  vehicleName: string,
  destinationUrl: string,
  partner?: string,
  surface?: string,
): void {
  const event = {
    event: 'monetization_cta_click' as const,
    ctaType,
    postId,
    vehicleCategory,
    vehicleName,
    destinationUrl,
    ...(partner && { partner }),
    ...(surface && { surface }),
    sessionAgeMs: getSessionAgeMs(),
    ...(getUtmAttribution() || {}),
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore — analytics should never break UX */ }
}

// ANALYTICS-031: Track Community & Garage Page Engagement — Session Depth,
// Return Visits, Feature Interaction Baseline.  Three thin events give us a
// baseline for how engaged community users are so we can measure future
// feature impact.

// --- Session depth ---
// Fired on visibilitychange/pagehide from any community page (garages, garage
// detail, explore).  `pagesViewed` counts distinct community routes hit in the
// current session (stored in sessionStorage).
const COMMUNITY_DEPTH_KEY = 'mw_community_depth';

function bumpCommunityDepth(): number {
  if (typeof sessionStorage === 'undefined') return 1;
  try {
    const cur = Number(sessionStorage.getItem(COMMUNITY_DEPTH_KEY)) || 0;
    const next = cur + 1;
    sessionStorage.setItem(COMMUNITY_DEPTH_KEY, String(next));
    return next;
  } catch {
    return 1;
  }
}

export function trackCommunitySessionDepth(
  page: 'garages' | 'garage_detail' | 'explore',
  dwellMs: number,
): void {
  if (dwellMs < 1000) return;
  const pagesViewed = bumpCommunityDepth();
  const event = {
    event: 'community_session_depth' as const,
    page,
    pagesViewed,
    dwellMs,
    sessionAgeMs: getSessionAgeMs(),
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore — analytics should never break UX */ }
}

// --- Return visits ---
// Fired once per session on the first community page load.  Compares current
// timestamp against localStorage `mw_community_last_visit` to classify the
// user as new, returning (<7d), or lapsed (≥7d).
const COMMUNITY_LAST_VISIT_KEY = 'mw_community_last_visit';
const COMMUNITY_VISIT_COUNT_KEY = 'mw_community_visit_count';

export function trackCommunityReturnVisit(
  landingPage: 'garages' | 'garage_detail' | 'explore',
): void {
  if (typeof localStorage === 'undefined') return;
  // Only fire once per session
  const sessionFlag = 'mw_community_return_fired';
  try {
    if (sessionStorage.getItem(sessionFlag)) return;
    sessionStorage.setItem(sessionFlag, '1');
  } catch { return; }

  let daysSinceLastVisit: number | null = null;
  let visitCount = 1;
  try {
    const last = Number(localStorage.getItem(COMMUNITY_LAST_VISIT_KEY));
    visitCount = (Number(localStorage.getItem(COMMUNITY_VISIT_COUNT_KEY)) || 0) + 1;
    if (last > 0) {
      daysSinceLastVisit = Math.round((Date.now() - last) / 86_400_000);
    }
    localStorage.setItem(COMMUNITY_LAST_VISIT_KEY, String(Date.now()));
    localStorage.setItem(COMMUNITY_VISIT_COUNT_KEY, String(visitCount));
  } catch { /* ignore */ }

  const visitorType: 'new' | 'returning' | 'lapsed' =
    daysSinceLastVisit === null ? 'new' :
    daysSinceLastVisit < 7 ? 'returning' : 'lapsed';

  const event = {
    event: 'community_return_visit' as const,
    landingPage,
    visitorType,
    daysSinceLastVisit,
    visitCount,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore — analytics should never break UX */ }
}

// --- Feature interaction baseline ---
// Lightweight event for key community interactions so we can build a baseline
// before optimising any specific feature.
export type CommunityInteraction =
  | 'post_view' | 'post_create' | 'comment' | 'upvote' | 'downvote'
  | 'share' | 'join' | 'leave' | 'follow' | 'unfollow' | 'search' | 'invite';

export function trackCommunityFeatureInteraction(
  interaction: CommunityInteraction,
  garageSlug: string,
  surface: 'garage_detail' | 'garages_directory' | 'explore' | 'feed',
): void {
  const event = {
    event: 'community_feature_interaction' as const,
    interaction,
    garageSlug,
    surface,
    sessionAgeMs: getSessionAgeMs(),
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore — analytics should never break UX */ }
}

// ---------------------------------------------------------------------------
// ANALYTICS-034: Vehicle Tag Click-Through Tracking
// ---------------------------------------------------------------------------

export function trackVehicleTagClick(
  vehicleSlug: string,
  sourceArticle: string,
  tagPosition: number,
): void {
  const event = {
    event: 'vehicle_tag_click' as const,
    vehicle_slug: vehicleSlug,
    source_article: sourceArticle,
    tag_position: tagPosition,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-036: Comparison Demand Signal Tracking
// ---------------------------------------------------------------------------

export function trackCompareIntent(
  vehicleSlug: string,
  sourcePage: string,
): void {
  const event = {
    event: 'compare_intent' as const,
    vehicle_slug: vehicleSlug,
    source_page: sourcePage,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackCompareSearch(
  query: string,
  category: 'car' | 'bike',
): void {
  const event = {
    event: 'compare_search' as const,
    query,
    category,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ---------------------------------------------------------------------------
// ANALYTICS-038: Tata Sierra EV Pre/Post Debut Engagement Tracker
// ---------------------------------------------------------------------------

const SIERRA_EV_TAGS = ['sierra-ev', 'tata-sierra', 'sierra-ev-2026'];
const SIERRA_DEBUT_DATE = new Date('2026-06-30T00:00:00+05:30');

export function isSierraEvArticle(tags: string[], title: string): boolean {
  const lowerTitle = title.toLowerCase();
  return tags.some((t) => SIERRA_EV_TAGS.includes(t.toLowerCase())) ||
    (lowerTitle.includes('sierra') && lowerTitle.includes('ev'));
}

export function trackSierraEvArticleView(
  postId: string,
  category: string,
  tags: string[],
  title: string,
): void {
  if (!isSierraEvArticle(tags, title)) return;

  const phase: 'pre' | 'post' = Date.now() < SIERRA_DEBUT_DATE.getTime() ? 'pre' : 'post';
  const event = {
    event: 'sierra_ev_article_view' as const,
    postId,
    category,
    phase,
    tags,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore */ }
}

// ANALYTICS-039: EV Subsidy Calculator usage event tracking
interface EvCalcEvent {
  event: 'ev_calc_start' | 'ev_calc_state_changed' | 'ev_calc_complete' | 'ev_calc_cta_click';
  city?: string;
  vehicleType?: string;
  preset?: string;
  isCustom?: boolean;
  totalSavings?: number;
  savingsPct?: number;
  ctaTarget?: string;
  timestamp: number;
}

export function trackEvCalcStart(): void {
  const event: EvCalcEvent = { event: 'ev_calc_start', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEvCalcStateChanged(city: string, vehicleType: string, preset: string, isCustom: boolean): void {
  const event: EvCalcEvent = { event: 'ev_calc_state_changed', city, vehicleType, preset, isCustom, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEvCalcComplete(city: string, vehicleType: string, preset: string, isCustom: boolean, totalSavings: number, savingsPct: number): void {
  const event: EvCalcEvent = { event: 'ev_calc_complete', city, vehicleType, preset, isCustom, totalSavings, savingsPct, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEvCalcCtaClick(ctaTarget: string): void {
  const event: EvCalcEvent = { event: 'ev_calc_cta_click', ctaTarget, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// ANALYTICS-045: EV Calculator step-by-step funnel tracking — measure drop-off at each stage
interface EvCalcFunnelEvent {
  event: 'ev_calc_funnel_city' | 'ev_calc_funnel_vehicle_type' | 'ev_calc_funnel_vehicle_select' | 'ev_calc_funnel_result_view' | 'ev_calc_funnel_subsidy_applied' | 'ev_calc_funnel_cta_click';
  step: 1 | 2 | 3 | 4 | 5 | 6;
  city?: string;
  vehicleType?: string;
  preset?: string;
  isCustom?: boolean;
  totalSavings?: number;
  savingsPct?: number;
  withScrappage?: boolean;
  ctaTarget?: string;
  timestamp: number;
}

export function trackEvCalcFunnelCity(city: string): void {
  const event: EvCalcFunnelEvent = { event: 'ev_calc_funnel_city', step: 1, city, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEvCalcFunnelVehicleType(city: string, vehicleType: string): void {
  const event: EvCalcFunnelEvent = { event: 'ev_calc_funnel_vehicle_type', step: 2, city, vehicleType, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEvCalcFunnelVehicleSelect(city: string, vehicleType: string, preset: string, isCustom: boolean): void {
  const event: EvCalcFunnelEvent = { event: 'ev_calc_funnel_vehicle_select', step: 3, city, vehicleType, preset, isCustom, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEvCalcFunnelResultView(city: string, vehicleType: string, preset: string, isCustom: boolean, totalSavings: number, savingsPct: number): void {
  const event: EvCalcFunnelEvent = { event: 'ev_calc_funnel_result_view', step: 4, city, vehicleType, preset, isCustom, totalSavings, savingsPct, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEvCalcFunnelSubsidyApplied(city: string, vehicleType: string, preset: string, isCustom: boolean, withScrappage: boolean): void {
  const event: EvCalcFunnelEvent = { event: 'ev_calc_funnel_subsidy_applied', step: 5, city, vehicleType, preset, isCustom, withScrappage, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEvCalcFunnelCtaClick(city: string, vehicleType: string, ctaTarget: string, totalSavings: number): void {
  const event: EvCalcFunnelEvent = { event: 'ev_calc_funnel_cta_click', step: 6, city, vehicleType, ctaTarget, totalSavings, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// COMMUNITY-002: Generic event emitter for lightweight custom events
export function trackEvent(eventName: string, props?: Record<string, unknown>): void {
  const event = { event: eventName, ...props, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch { /* silently ignore */ }
}

// ANALYTICS-040: Notify Me widget funnel events — impression, click, submit attempt
interface NotifyMeWidgetEvent {
  event: 'notify_me_impression' | 'notify_me_click' | 'notify_me_submit';
  vehicleSlug: string;
  vehicleType: string;
  brand?: string;
  timestamp: number;
}

export function trackNotifyMeImpression(vehicleSlug: string, vehicleType: string, brand?: string): void {
  const event: NotifyMeWidgetEvent = {
    event: 'notify_me_impression',
    vehicleSlug,
    vehicleType,
    brand,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackNotifyMeClick(vehicleSlug: string, vehicleType: string, brand?: string): void {
  const event: NotifyMeWidgetEvent = {
    event: 'notify_me_click',
    vehicleSlug,
    vehicleType,
    brand,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackNotifyMeSubmit(vehicleSlug: string, vehicleType: string, brand?: string): void {
  const event: NotifyMeWidgetEvent = {
    event: 'notify_me_submit',
    vehicleSlug,
    vehicleType,
    brand,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// ANALYTICS-043: Feed source diversity — fires when user reads articles from
// 3+ distinct sources in one session, measuring breadth of feed engagement.
export function trackFeedSourceDiversity(
  distinctSources: number,
  sourceNames: string[],
): void {
  const event = {
    event: 'feed_source_diversity',
    distinctSources,
    sourceNames,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// ANALYTICS-041: Affiliate CPL click tracking — fires sendBeacon when user
// clicks a CPL/affiliate CTA (e.g. "Get Best Offer") on vehicle spec pages.
// Pre-requisite for MON-006 attribution.
export function trackAffiliateCPLClick(
  vehicleSlug: string,
  partner: string,
  placement: string,
  category: string,
): void {
  const event = {
    event: 'affiliate_cpl_click',
    vehicleSlug,
    partner,
    placement,
    category,
    segment: toVehicleSegment(category),
    sourcePage: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// ANALYTICS-046: PolicyBazaar Insurance CPL Widget — dedicated impression + click
// tracking with plan type, placement, and vehicle category for CPL attribution.
export function trackPolicybazaarCPLEvent(
  vehicleSlug: string,
  action: 'impression' | 'click',
  placement: string,
  vehicleCategory: string,
  planType?: string,
): void {
  const event = {
    event: `policybazaar_cpl_${action}`,
    vehicleSlug,
    partner: 'policybazaar',
    placement,
    vehicleCategory,
    ...(planType && { planType }),
    sourcePage: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// GROWTH-005: EV Charging Station Finder widget analytics.
export function trackChargingWidgetImpression(vehicleSlug: string): void {
  const event = {
    event: 'charging_widget_impression',
    vehicleSlug,
    sourcePage: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackChargingStationClick(vehicleSlug: string, stationId: string, network: string): void {
  const event = {
    event: 'charging_station_click',
    vehicleSlug,
    stationId,
    network,
    sourcePage: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// GROWTH-009: Offline reading analytics
export function trackOfflineFeedView(cachedPostCount: number): void {
  const event = {
    event: 'offline_feed_view',
    cachedPostCount,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackOfflineArticleRead(postId: string, postTitle: string): void {
  const event = {
    event: 'offline_article_read',
    postId,
    postTitle,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// ---------------------------------------------------------------------------
// ANALYTICS-050: Garage Member Retention Tracking
// Measures returning member visits to garages they've already joined.
// Fires once per garage per session to avoid double-counting.
// ---------------------------------------------------------------------------

const _garageReturnFiredThisSession = new Set<string>();

export type GarageReturnSource = 'direct' | 'feed' | 'notification' | 'spotlight' | 'activity_pulse' | 'search';

export function trackGarageReturn(
  garageId: string,
  garageSlug: string,
  source: GarageReturnSource,
  daysSinceJoin: number | null,
): void {
  if (_garageReturnFiredThisSession.has(garageId)) return;
  _garageReturnFiredThisSession.add(garageId);

  const event = {
    event: 'garage_member_return' as const,
    garageId,
    garageSlug,
    source,
    daysSinceJoin,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackGarageReturnDwell(
  garageId: string,
  garageSlug: string,
  dwellMs: number,
  postsViewed: number,
): void {
  const event = {
    event: 'garage_return_dwell' as const,
    garageId,
    garageSlug,
    dwellMs,
    postsViewed,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// COMM-017: Garage activity feed interactions
// ANALYTICS-051: Dealer Lead CTA — impressions, clicks, form-starts per article page
export function trackDealerLeadCtaImpression(postId: string, brand: string, placement: string): void {
  const event = { event: 'dealer_lead_cta_impression' as const, postId, brand, placement, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackDealerLeadCtaClick(postId: string, brand: string, placement: string): void {
  const event = { event: 'dealer_lead_cta_click' as const, postId, brand, placement, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackDealerLeadFormStart(postId: string, brand: string, placement: string): void {
  const event = { event: 'dealer_lead_form_start' as const, postId, brand, placement, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackGarageActivityFeedViewed(garageSlug: string, eventCount: number): void {
  const event = { event: 'garage_activity_feed_viewed' as const, garageSlug, eventCount, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackGarageActivityItemClicked(garageSlug: string, itemType: 'join' | 'post' | 'comment'): void {
  const event = { event: 'garage_activity_item_clicked' as const, garageSlug, itemType, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// COMM-010: Garage reading activity stream analytics
export function trackGarageReadingStreamViewed(garageSlug: string, itemCount: number): void {
  const event = { event: 'garage_reading_stream_viewed' as const, garageSlug, itemCount, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackGarageReadingStreamItemClicked(garageSlug: string, postId: string, interactionType: string): void {
  const event = { event: 'garage_reading_stream_item_clicked' as const, garageSlug, postId, interactionType, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// GROWTH-011: MotoCard shareable identity card analytics
export type MotoCardShareChannel = 'copy_link' | 'twitter' | 'whatsapp';

export function trackMotoCardView(username: string): void {
  const event = { event: 'motocard_view' as const, username, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackMotoCardShare(username: string, channel: MotoCardShareChannel): void {
  const event = { event: 'motocard_share' as const, username, channel, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// ANALYTICS-053: Vehicle Launch Alert Subscription Tracking — launches page funnel
export function trackLaunchPageView(filterCategory: string, launchCount: number): void {
  const event = { event: 'launch_page_view' as const, filterCategory, launchCount, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackLaunchCardImpression(vehicleSlug: string, vehicleName: string, brand: string, category: string, status: string): void {
  const event = { event: 'launch_card_impression' as const, vehicleSlug, vehicleName, brand, category, status, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackLaunchCardClick(vehicleSlug: string, vehicleName: string, brand: string, category: string): void {
  const event = { event: 'launch_card_click' as const, vehicleSlug, vehicleName, brand, category, timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// MON-016: Dealer CRM Lead Handoff — launch-day conversion funnel
export function trackLaunchDealerCtaImpression(vehicleSlug: string, brand: string): void {
  const event = { event: 'launch_dealer_cta_impression' as const, vehicleSlug, brand, placement: 'launches', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackLaunchDealerCtaClick(vehicleSlug: string, brand: string): void {
  const event = { event: 'launch_dealer_cta_click' as const, vehicleSlug, brand, placement: 'launches', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackLaunchDealerFormStart(vehicleSlug: string, brand: string): void {
  const event = { event: 'launch_dealer_form_start' as const, vehicleSlug, brand, placement: 'launches', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackLaunchDealerConvert(vehicleSlug: string, brand: string): void {
  const event = { event: 'launch_dealer_convert' as const, vehicleSlug, brand, placement: 'launches', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// MON-017: Test Drive Booking CTA — launch page conversion funnel
export function trackTestDriveBookingImpression(vehicleSlug: string, brand: string): void {
  const event = { event: 'test_drive_booking_impression' as const, vehicleSlug, brand, placement: 'launches', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackTestDriveBookingClick(vehicleSlug: string, brand: string): void {
  const event = { event: 'test_drive_booking_click' as const, vehicleSlug, brand, placement: 'launches', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackTestDriveBookingFormStart(vehicleSlug: string, brand: string): void {
  const event = { event: 'test_drive_booking_form_start' as const, vehicleSlug, brand, placement: 'launches', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackTestDriveBookingSubmit(vehicleSlug: string, brand: string): void {
  const event = { event: 'test_drive_booking_submit' as const, vehicleSlug, brand, placement: 'launches', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// MON-018: Car Loan EMI Calculator — bank affiliate funnel
export function trackEmiCalcStart(): void {
  const event = { event: 'emi_calc_start' as const, placement: 'tools', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEmiCalcStateChanged(vehicleType: string, presetSlug: string, isCustom: boolean, loanAmount: number, rate: number, tenure: number): void {
  const event = { event: 'emi_calc_state_changed' as const, vehicleType, presetSlug, isCustom, loanAmount, rate, tenure, placement: 'tools', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEmiCalcBankCtaImpression(bankId: string): void {
  const event = { event: 'emi_calc_bank_cta_impression' as const, bankId, placement: 'tools', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEmiCalcBankCtaClick(bankId: string, bankName: string, loanAmount: number, rate: number, tenure: number): void {
  const event = { event: 'emi_calc_bank_cta_click' as const, bankId, bankName, loanAmount, rate, tenure, placement: 'tools', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

// ANALYTICS-054: Garage Add-Vehicle Funnel — garage_page_view → add_vehicle_click
// → add_vehicle_success. Measures the conversion rate from viewing a garage to
// successfully adding a vehicle, segmented by vehicle type and source surface.
export function trackGarageAddVehicleView(garageSlug: string, memberCount: number): void {
  const event = {
    event: 'garage_add_vehicle_view' as const,
    garageSlug,
    memberCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageAddVehicleClick(garageSlug: string, vehicleType: 'cars' | 'bikes' | 'scooters', source: 'garage_detail' | 'garage_sidebar' | 'onboarding'): void {
  const event = {
    event: 'garage_add_vehicle_click' as const,
    garageSlug,
    vehicleType,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageAddVehicleSuccess(garageSlug: string, vehicleSlug: string, vehicleType: 'cars' | 'bikes' | 'scooters', source: 'garage_detail' | 'garage_sidebar' | 'onboarding'): void {
  const event = {
    event: 'garage_add_vehicle_success' as const,
    garageSlug,
    vehicleSlug,
    vehicleType,
    source,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.(
      '/api/analytics/events',
      JSON.stringify(event),
    );
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// COMM-011: "Members also in" widget tracking
export function trackGarageAlsoInViewed(garageSlug: string, count: number): void {
  const event = {
    event: 'garage_also_in_viewed' as const,
    garageSlug,
    count,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackGarageAlsoInClicked(sourceGarageSlug: string, targetGarageSlug: string, position: number): void {
  const event = {
    event: 'garage_also_in_clicked' as const,
    sourceGarageSlug,
    targetGarageSlug,
    position,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-055: Brand Hub Page Engagement Tracking
export function trackBrandHubFilterChipClick(brand: string, category: string, model: string): void {
  const event = {
    event: 'brand_hub_filter_chip_click' as const,
    brand,
    category,
    model,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackBrandHubVehicleCardClick(brand: string, category: string, postId: string, position: number): void {
  const event = {
    event: 'brand_hub_vehicle_card_click' as const,
    brand,
    category,
    postId,
    position,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackBrandHubScrollDepth(brand: string, category: string, depth: 25 | 50 | 75 | 100): void {
  const event = {
    event: 'brand_hub_scroll_depth' as const,
    brand,
    category,
    depth,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackLaunchAlertBannerImpression(vehicleSlug: string, vehicleName: string, daysLeft: number): void {
  const event = {
    event: 'launch_alert_banner_impression' as const,
    vehicleSlug,
    vehicleName,
    daysLeft,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackLaunchAlertBannerSubmit(vehicleSlug: string, vehicleName: string, daysLeft: number): void {
  const event = {
    event: 'launch_alert_banner_submit' as const,
    vehicleSlug,
    vehicleName,
    daysLeft,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackLaunchAlertBannerDismiss(vehicleSlug: string): void {
  const event = {
    event: 'launch_alert_banner_dismiss' as const,
    vehicleSlug,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-064: Launch Calendar (upcoming) page engagement
export function trackLaunchCalendarView(monthCount: number, launchCount: number): void {
  const event = {
    event: 'launch_calendar_viewed' as const,
    monthCount,
    launchCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackLaunchCalendarMonthClick(month: string, launchCount: number): void {
  const event = {
    event: 'launch_calendar_month_clicked' as const,
    month,
    launchCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackLaunchCalendarCardClick(vehicleSlug: string, vehicleName: string, brand: string, daysUntilLaunch: number): void {
  const event = {
    event: 'launch_calendar_card_clicked' as const,
    vehicleSlug,
    vehicleName,
    brand,
    daysUntilLaunch,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackLaunchCalendarFilter(filterCategory: string): void {
  const event = {
    event: 'launch_calendar_filtered' as const,
    filterCategory,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ENGAGE-018: Weekly Recap Widget — "You Might Have Missed" impression + click tracking
export function trackWeeklyRecapImpression(articleCount: number): void {
  const event = {
    event: 'weekly_recap_impression' as const,
    articleCount,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackWeeklyRecapClick(articleId: string, category: string, position: number): void {
  const event = {
    event: 'weekly_recap_click' as const,
    articleId,
    category,
    position,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-065: Track PM E-DRIVE Subsidy Deadline Banner Impressions and CTR
export function trackSubsidyCountdownImpression(vehicleSlug: string, daysRemaining: number, variant: 'banner' | 'inline'): void {
  const event = {
    event: 'subsidy_countdown_impression' as const,
    vehicleSlug,
    daysRemaining,
    variant,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

export function trackSubsidyCountdownClick(vehicleSlug: string, daysRemaining: number, variant: 'banner' | 'inline'): void {
  const event = {
    event: 'subsidy_countdown_click' as const,
    vehicleSlug,
    daysRemaining,
    variant,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// MON-020: Subsidy banner finance CTA tracking — insurance + loan clicks from countdown widget
export function trackSubsidyFinanceCTAClick(
  vehicleSlug: string,
  ctaType: 'insurance' | 'ev-loan',
  partner: string,
  daysRemaining: number,
  vehicleCategory: 'ev-2w' | 'ev-car',
): void {
  const event = {
    event: 'subsidy_finance_cta_click' as const,
    vehicleSlug,
    ctaType,
    partner,
    daysRemaining,
    vehicleCategory,
    sourcePage: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackSubsidyFinanceCTAImpression(
  vehicleSlug: string,
  daysRemaining: number,
  vehicleCategory: 'ev-2w' | 'ev-car',
): void {
  const event = {
    event: 'subsidy_finance_cta_impression' as const,
    vehicleSlug,
    daysRemaining,
    vehicleCategory,
    sourcePage: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackBrandHubShare(brand: string, category: string, channel: string): void {
  const event = {
    event: 'brand_hub_share' as const,
    brand,
    category,
    channel,
    timestamp: Date.now(),
  };

  if (import.meta.env.DEV) {
    console.log('[analytics]', event);
  }

  try {
    navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event));
  } catch {
    // silently ignore — analytics should never break UX
  }
}

// ANALYTICS-067: EMI Calculator → Dealer Lead conversion funnel tracking.
// Stores a session flag when the user calculates EMI, then enriches dealer
// lead events so we can measure how many EMI-calc users convert to leads.
const EMI_CALC_USED_KEY = 'mw_emi_calc_used';

/** Call after the user calculates EMI on any surface (spec page widget or /tools). */
export function markEmiCalcUsed(vehicleSlug: string, placement: string): void {
  try {
    const data = JSON.stringify({ vehicleSlug, placement, ts: Date.now() });
    sessionStorage.setItem(EMI_CALC_USED_KEY, data);
  } catch { /* sessionStorage blocked */ }
}

/** Returns the EMI-calc session context, or null if user hasn't used EMI calc. */
export function getEmiCalcFunnelContext(): { vehicleSlug: string; placement: string; ts: number } | null {
  try {
    const raw = sessionStorage.getItem(EMI_CALC_USED_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

/** Fires when a dealer lead CTA enters viewport and the user previously used EMI calc. */
export function trackEmiFunnelDealerImpression(dealerSlug: string, brand: string, dealerPlacement: string): void {
  const ctx = getEmiCalcFunnelContext();
  if (!ctx) return;
  const event = {
    event: 'emi_funnel_dealer_impression' as const,
    dealerSlug,
    brand,
    dealerPlacement,
    emiVehicleSlug: ctx.vehicleSlug,
    emiPlacement: ctx.placement,
    emiToImpressionMs: Date.now() - ctx.ts,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* ignore */ }
}

/** Fires when a user who previously used EMI calc submits a dealer lead form. */
export function trackEmiFunnelDealerConvert(dealerSlug: string, brand: string, dealerPlacement: string, leadType: string): void {
  const ctx = getEmiCalcFunnelContext();
  if (!ctx) return;
  const event = {
    event: 'emi_funnel_dealer_convert' as const,
    dealerSlug,
    brand,
    dealerPlacement,
    leadType,
    emiVehicleSlug: ctx.vehicleSlug,
    emiPlacement: ctx.placement,
    emiToConvertMs: Date.now() - ctx.ts,
    timestamp: Date.now(),
  };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* ignore */ }
}

// MON-022: EV Insurance Savings Calculator — insurance partner affiliate funnel
export function trackEVInsuranceCalcStart(): void {
  const event = { event: 'ev_insurance_calc_start' as const, placement: 'tools', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEVInsuranceCalcStateChanged(vehicleType: string, presetSlug: string, vehiclePrice: number, annualKm: number, coverageType: string): void {
  const event = { event: 'ev_insurance_calc_state_changed' as const, vehicleType, presetSlug, vehiclePrice, annualKm, coverageType, placement: 'tools', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEVInsuranceCalcSavingsShown(evPremium: number, icePremium: number, savings: number, vehicleSlug: string): void {
  const event = { event: 'ev_insurance_calc_savings_shown' as const, evPremium, icePremium, savings, vehicleSlug, placement: 'tools', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEVInsurancePartnerImpression(partner: string, vehicleSlug: string): void {
  const event = { event: 'ev_insurance_partner_impression' as const, partner, vehicleSlug, placement: 'tools', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}

export function trackEVInsurancePartnerClick(partner: string, vehicleSlug: string, estimatedPremium: number, destinationUrl: string): void {
  const event = { event: 'ev_insurance_partner_click' as const, partner, vehicleSlug, estimatedPremium, destinationUrl, placement: 'tools', timestamp: Date.now() };
  if (import.meta.env.DEV) console.log('[analytics]', event);
  try { navigator.sendBeacon?.('/api/analytics/events', JSON.stringify(event)); } catch { /* silently ignore */ }
}
